import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Check, X, Clock, Mail, UserCheck, UserX, LogOut, RefreshCw, AlertCircle, AlertTriangle, CheckCircle, CheckCircle2, Ban, Wifi, WifiOff, Zap, CreditCard, Calendar, DollarSign, TrendingUp, Trophy, ChevronDown, ChevronUp, BarChart3, Tag, Megaphone, Gift, Trash2, ToggleLeft, ToggleRight, Image, MessageSquare, Eye, EyeOff, XCircle, Tv, Link, Play, Users2, Smartphone, Upload, Send, Star, MessageCircle, MapPin, Phone, User, Ticket, Edit, Plus, Save, Trash, FileText, Loader2, Archive, Timer, GraduationCap, Copy, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast, Toaster } from 'sonner';
import { AdminMessengerModal, AdminChatBubble } from '../components/MessengerModal';
import { getCountryFlag } from '../components/GuestAuthModal';
import AdminLiveChatPanel from '../components/AdminLiveChatPanel';

// API URL configuration
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [guests, setGuests] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    pending_verification: 0,
    pending_approval: 0,
    approved: 0,
    rejected: 0,
    blocked: 0,
    online: 0,
    trial_users: 0,
    paid_users: 0,
    trial_expired: 0
  });
  const [processing, setProcessing] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [selectedStatsPeriod, setSelectedStatsPeriod] = useState({});
  
  // Promotion state
  const [promoEnabled, setPromoEnabled] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoText, setPromoText] = useState('');
  const [promoColor, setPromoColor] = useState('yellow');
  const [promoDiscount, setPromoDiscount] = useState(10); // Discount percentage
  const [promoEndTime, setPromoEndTime] = useState(''); // End time for countdown
  const [savingPromo, setSavingPromo] = useState(false);
  
  // Referral codes state
  const [referralCodes, setReferralCodes] = useState([]);
  const [newRefCode, setNewRefCode] = useState('');
  const [newRefDiscount, setNewRefDiscount] = useState(10);
  const [newRefName, setNewRefName] = useState('');
  const [newRefDesc, setNewRefDesc] = useState('');
  const [savingRef, setSavingRef] = useState(false);
  
  // Payment proofs state
  const [paymentProofs, setPaymentProofs] = useState([]);
  const [unreadProofs, setUnreadProofs] = useState(0);
  const [selectedProof, setSelectedProof] = useState(null);
  const [showPaymentProofs, setShowPaymentProofs] = useState(false); // Collapsed by default

  // Pending email codes (SendGrid failsafe) state
  const [pendingCodes, setPendingCodes] = useState({ verification_codes: [], reset_codes: [], counts: { verification_active: 0, reset_active: 0 } });
  const [showPendingCodes, setShowPendingCodes] = useState(false);
  const [loadingPendingCodes, setLoadingPendingCodes] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [showExpiredCodes, setShowExpiredCodes] = useState(false);
  
  // Education TV state
  const [educationContent, setEducationContent] = useState([]);
  const [newEduTitle, setNewEduTitle] = useState('');
  const [newEduDescription, setNewEduDescription] = useState('');
  const [newEduUrl, setNewEduUrl] = useState('');
  const [newEduTargetType, setNewEduTargetType] = useState('all');
  const [newEduTargetUsers, setNewEduTargetUsers] = useState('');
  const [savingEdu, setSavingEdu] = useState(false);
  
  // Community Chat Management state
  const [chatMessages, setChatMessages] = useState([]);
  const [suspendedUsers, setSuspendedUsers] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [loadingSuspended, setLoadingSuspended] = useState(false);
  
  // Collapsible sections state
  const [educationTVCollapsed, setEducationTVCollapsed] = useState(true);
  const [communityChatCollapsed, setCommunityChatCollapsed] = useState(true);
  
  // Bonus days grant state
  const [showBonusModal, setShowBonusModal] = useState(null); // user id
  const [bonusDays, setBonusDays] = useState(7);

  // Global Ads state
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [togglingAds, setTogglingAds] = useState(false);

  // Market Alert state (for market manipulation warning)
  const [marketAlertEnabled, setMarketAlertEnabled] = useState(false);
  const [togglingMarketAlert, setTogglingMarketAlert] = useState(false);

  // PWA Update state
  const [appVersion, setAppVersion] = useState('1.0.0');
  const [lastUpdatePushed, setLastUpdatePushed] = useState(null);
  const [updateMessage, setUpdateMessage] = useState('');
  const [pushingUpdate, setPushingUpdate] = useState(false);

  // Messenger state
  const [showMessenger, setShowMessenger] = useState(false);
  const [messengerUser, setMessengerUser] = useState(null);

  // Support Tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [showSupportTickets, setShowSupportTickets] = useState(false);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);

  // Reviews Management state
  const [reviews, setReviews] = useState([]);
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);
  const [showReviewsPanel, setShowReviewsPanel] = useState(false);

  // Coaching Management state
  const [showCoachingPanel, setShowCoachingPanel] = useState(false);
  const [coachingBookings, setCoachingBookings] = useState([]);
  const [coachingAvailability, setCoachingAvailability] = useState([]);
  const [coachingViewMonth, setCoachingViewMonth] = useState(new Date().getMonth());
  const [coachingViewYear, setCoachingViewYear] = useState(new Date().getFullYear());
  const [newAvailDate, setNewAvailDate] = useState('');
  const [newAvailTimes, setNewAvailTimes] = useState('');
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [coachingTickets, setCoachingTickets] = useState([]);
  const [pendingCoachingCount, setPendingCoachingCount] = useState(0);

  // Products Management state
  const [showProductsPanel, setShowProductsPanel] = useState(false);
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productForm, setProductForm] = useState({
    product_id: '',
    name: '',
    category: 'signal_generator',
    short_description: '',
    full_description: '',
    image_url: '',
    price: 0,
    price_type: 'monthly',
    secondary_price: '',
    secondary_price_type: '',
    features: [],
    is_active: true,
    display_order: 0,
    sub_products: [],
    payment_link: '',
    pro_upgrade_payment_link: ''
  });
  const [newFeature, setNewFeature] = useState('');

  // Site Content Management state
  const [showSiteContentPanel, setShowSiteContentPanel] = useState(false);
  const [siteContent, setSiteContent] = useState({
    aboutUs: { title: 'About Bonbot', content: '' },
    contactUs: { title: 'Contact Us', email: '', phone: '', address: '' },
    serviceTerms: '',
    refundPolicy: ''
  });
  const [savingSiteContent, setSavingSiteContent] = useState(false);

  // Staff Management state
  const [showStaffPanel, setShowStaffPanel] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffRole, setNewStaffRole] = useState('moderator');
  const [invitingStaff, setInvitingStaff] = useState(false);
  const [adminRole, setAdminRole] = useState('admin'); // Current user's role

  // Subscription Calendar state
  const [showSubscriptionCalendar, setShowSubscriptionCalendar] = useState(false);
  const [subscriptionCalendar, setSubscriptionCalendar] = useState([]);
  const [subscriptionSummary, setSubscriptionSummary] = useState({ total: 0, expired: 0, expiring_soon: 0, warning: 0, active: 0 });
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [selectedUserForExpiry, setSelectedUserForExpiry] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [settingExpiry, setSettingExpiry] = useState(false);
  const [subscriptionFilter, setSubscriptionFilter] = useState('all'); // 'all', 'expired', 'expiring_soon', 'active'

  // Support Agents state
  const [showSupportAgentsPanel, setShowSupportAgentsPanel] = useState(false);
  const [supportAgents, setSupportAgents] = useState([]);
  const [loadingSupportAgents, setLoadingSupportAgents] = useState(false);
  const [newAgentForm, setNewAgentForm] = useState({ username: '', password: '', name: '', email: '' });
  const [creatingAgent, setCreatingAgent] = useState(false);

  // Send Login Email state
  const [loginEmailAddress, setLoginEmailAddress] = useState('');
  const [sendingLoginEmail, setSendingLoginEmail] = useState(false);

  // Users & Purchases state
  const [showUsersPurchasesPanel, setShowUsersPurchasesPanel] = useState(false);
  const [usersPurchases, setUsersPurchases] = useState([]);
  const [usersPurchasesSummary, setUsersPurchasesSummary] = useState({ total_users: 0, paid_users: 0, free_users: 0 });
  const [loadingUsersPurchases, setLoadingUsersPurchases] = useState(false);
  const [usersPurchasesFilter, setUsersPurchasesFilter] = useState('all'); // 'all', 'paid', 'free'
  const [usersPurchasesCategory, setUsersPurchasesCategory] = useState('all'); // 'all', 'coaching', 'subscriptions'

  // Trade History state
  const [tradeHistory, setTradeHistory] = useState([]);
  const [tradeStats, setTradeStats] = useState(null);
  const [showTradeHistory, setShowTradeHistory] = useState(false); // Collapsed by default
  const [tradeFilters, setTradeFilters] = useState({
    user_email: '',
    symbol: '',
    result: ''
  });
  const [tradePagination, setTradePagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false
  });
  const [loadingTrades, setLoadingTrades] = useState(false);

  // Marketing Broadcast state
  const [marketingCollapsed, setMarketingCollapsed] = useState(true);
  const [mkDescription, setMkDescription] = useState('');
  const [mkKeywords, setMkKeywords] = useState('');
  const [mkTone, setMkTone] = useState('friendly');
  const [mkCtaUrl, setMkCtaUrl] = useState('');
  const [mkCtaLabel, setMkCtaLabel] = useState('');
  const [mkSegment, setMkSegment] = useState('approved');
  const [mkSubject, setMkSubject] = useState('');
  const [mkBodyHtml, setMkBodyHtml] = useState('');
  const [mkWrappedHtml, setMkWrappedHtml] = useState('');
  const [mkPreviewText, setMkPreviewText] = useState('');
  const [mkGenerating, setMkGenerating] = useState(false);
  const [mkSending, setMkSending] = useState(false);
  const [mkTestEmail, setMkTestEmail] = useState('');
  const [mkAudienceCounts, setMkAudienceCounts] = useState({});
  const [mkBroadcasts, setMkBroadcasts] = useState([]);
  const [mkShowHistory, setMkShowHistory] = useState(false);
  const [mkSelectedBroadcast, setMkSelectedBroadcast] = useState(null);
  // Debounced body so the preview iframe doesn't re-render on every keystroke
  const [mkDebouncedBody, setMkDebouncedBody] = useState('');
  // Favorites
  const [mkFavorites, setMkFavorites] = useState([]);
  const [mkShowFavorites, setMkShowFavorites] = useState(false);
  const [mkSavingFav, setMkSavingFav] = useState(false);
  // Modals (window.prompt/confirm are blocked inside iframe previews)
  const [mkFavNameModal, setMkFavNameModal] = useState(false);
  const [mkFavName, setMkFavName] = useState('');
  const [mkConfirmSend, setMkConfirmSend] = useState(false);
  const [mkConfirmDeleteBroadcast, setMkConfirmDeleteBroadcast] = useState(null);
  const [mkConfirmDeleteFavorite, setMkConfirmDeleteFavorite] = useState(null);

  // ============ Trader Scorecards (Admin View) ============
  const [scCollapsed, setScCollapsed] = useState(true);
  const [scUsers, setScUsers] = useState([]);
  const [scLoadingUsers, setScLoadingUsers] = useState(false);
  const [scSelectedUser, setScSelectedUser] = useState(null); // email
  const [scSessions, setScSessions] = useState([]);
  const [scLoadingSessions, setScLoadingSessions] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMkDebouncedBody(mkBodyHtml), 400);
    return () => clearTimeout(t);
  }, [mkBodyHtml]);

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    verifyAdmin(adminToken);
  }, []);

  // Fetch subscription calendar when panel is opened
  useEffect(() => {
    if (showSubscriptionCalendar && subscriptionCalendar.length === 0) {
      fetchSubscriptionCalendar();
    }
  }, [showSubscriptionCalendar]);

  const verifyAdmin = async (token) => {
    try {
      const response = await fetch(`${API}/admin/verify?token=${token}`);
      const data = await response.json();
      
      if (data.valid) {
        setAdmin(data.admin);
        setAdminRole(data.admin?.role || 'admin');
        await fetchGuests(token);
        await fetchPromotion(token);
        await fetchAdSettings(token);
        await fetchMarketAlertSettings(token);
        await fetchAppVersion(token);
        await fetchReferralCodes(token);
        await fetchPaymentProofs(token);
        await fetchEducationContent(token);
        await fetchTradeHistory(token);
        await fetchTradeStats(token);
        await fetchReviews();
        await fetchSupportTickets();
        await fetchCoachingData(token);
        await fetchProducts(token);
        await fetchSiteContent();
        await fetchStaffList(token);
        await fetchUsersPurchases(token);
      } else {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin');
      }
    } catch (err) {
      toast.error('Failed to verify admin session');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is admin (not moderator)
  const isAdmin = () => adminRole === 'admin';

  // Check if action is allowed for current role
  const canModify = (action) => {
    if (adminRole === 'admin') return true;
    // Moderator allowed actions
    const moderatorActions = ['verify_payment', 'approve_payment', 'answer_support', 'pause_user'];
    return moderatorActions.includes(action);
  };

  // Fetch staff list
  const fetchStaffList = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/staff?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setStaffList(data.staff || []);
      }
    } catch (err) {
      console.error('Failed to fetch staff list:', err);
    }
  };

  // Fetch users with their purchases
  const fetchUsersPurchases = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    setLoadingUsersPurchases(true);
    try {
      const response = await fetch(`${API}/admin/users-purchases?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setUsersPurchases(data.users || []);
        setUsersPurchasesSummary(data.summary || { total_users: 0, paid_users: 0, free_users: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch users purchases:', err);
    } finally {
      setLoadingUsersPurchases(false);
    }
  };

  // Invite new staff
  const inviteStaff = async () => {
    if (!newStaffEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInvitingStaff(true);
    try {
      const response = await fetch(`${API}/admin/staff/invite?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newStaffEmail.trim(),
          role: newStaffRole
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Staff member invited! Temp password: ${data.staff.temp_password}`);
        setNewStaffEmail('');
        await fetchStaffList();
      } else {
        toast.error(data.message || 'Failed to invite staff');
      }
    } catch (err) {
      toast.error('Failed to invite staff member');
    } finally {
      setInvitingStaff(false);
    }
  };

  // Send Login Email to user
  const sendLoginEmail = async () => {
    if (!loginEmailAddress.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingLoginEmail(true);
    try {
      const response = await fetch(`${API}/admin/send-login-email?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmailAddress.trim().toLowerCase() })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Login email sent to ${loginEmailAddress}!`);
        setLoginEmailAddress('');
      } else {
        toast.error(data.message || 'Failed to send login email');
      }
    } catch (err) {
      toast.error('Failed to send login email');
    } finally {
      setSendingLoginEmail(false);
    }
  };

  // Delete staff member
  const deleteStaff = async (email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from staff?`)) return;
    
    try {
      const response = await fetch(`${API}/admin/staff/${encodeURIComponent(email)}?token=${localStorage.getItem('adminToken')}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Staff member removed');
        await fetchStaffList();
      } else {
        toast.error(data.message || 'Failed to remove staff');
      }
    } catch (err) {
      toast.error('Failed to remove staff member');
    }
  };

  // ============ Marketing Broadcast Functions ============
  const fetchMarketingAudience = async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/audience-counts?token=${localStorage.getItem('adminToken')}`);
      const data = await res.json();
      if (data.success) setMkAudienceCounts(data.counts || {});
    } catch (err) {
      console.error('Failed to load audience counts', err);
    }
  };

  const fetchMarketingBroadcasts = async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/broadcasts?token=${localStorage.getItem('adminToken')}`);
      const data = await res.json();
      if (data.success) setMkBroadcasts(data.broadcasts || []);
    } catch (err) {
      console.error('Failed to load broadcast history', err);
    }
  };

  const generateMarketingEmail = async () => {
    if (!mkDescription.trim() || !mkKeywords.trim()) {
      toast.error('Please enter both a description and keywords');
      return;
    }
    setMkGenerating(true);
    try {
      const res = await fetch(`${API}/admin/marketing/generate?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: mkDescription,
          keywords: mkKeywords,
          tone: mkTone,
          cta_url: mkCtaUrl || null,
          cta_label: mkCtaLabel || null
        })
      });
      const data = await res.json();
      if (data.success) {
        setMkSubject(data.subject || '');
        setMkBodyHtml(data.body_html || '');
        setMkWrappedHtml(data.wrapped_html || '');
        setMkPreviewText(data.preview_text || '');
        if (!mkCtaLabel && data.cta_label) setMkCtaLabel(data.cta_label);
        toast.success('Draft generated! Review and edit before sending.');
      } else {
        toast.error(data.message || 'Failed to generate email');
      }
    } catch (err) {
      toast.error('Failed to generate email');
    } finally {
      setMkGenerating(false);
    }
  };

  const rebuildWrappedHtml = (bodyHtml) => {
    const cta = mkCtaUrl ? `
      <div style="text-align: center; margin: 32px 0;">
        <a href="${mkCtaUrl}" style="display: inline-block; background: linear-gradient(135deg, #f59e0b, #ef4444); color: white; padding: 16px 44px; text-decoration: none; border-radius: 999px; font-weight: bold; font-size: 16px;">${mkCtaLabel || 'Learn More'}</a>
      </div>` : '';
    return `<html><body style="margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;background:#0f172a;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background:#0f172a;padding:32px 16px;"><tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;background:#1e293b;border-radius:16px;overflow:hidden;">
      <tr><td style="padding:28px 32px 8px;text-align:center;">
        <h1 style="margin:0;color:#f59e0b;font-size:24px;letter-spacing:1px;">FORTHEWISEPRO</h1>
        <p style="margin:4px 0 0 0;color:#94a3b8;font-size:12px;text-transform:uppercase;letter-spacing:2px;">Smart Trading Education</p>
      </td></tr>
      <tr><td style="padding:16px 32px 8px;color:#e2e8f0;font-size:16px;line-height:1.6;">${bodyHtml}</td></tr>
      <tr><td style="padding:0 32px 24px;">${cta}</td></tr>
      <tr><td style="padding:16px 32px 28px;text-align:center;border-top:1px solid #334155;">
        <p style="margin:8px 0 0 0;color:#64748b;font-size:11px;">FORTHEWISEPRO LLC &middot; Smart Trading Education<br/>You are receiving this because you signed up at forthewisepro.com.</p>
      </td></tr></table></td></tr></table></body></html>`;
  };

  const sendMarketingBroadcast = async (isTest = false) => {
    if (!mkSubject.trim() || !mkBodyHtml.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    if (isTest && !mkTestEmail.trim()) {
      toast.error('Enter a test email address');
      return;
    }
    if (!isTest) {
      // Open confirm modal instead of window.confirm (blocked in iframes)
      setMkConfirmSend(true);
      return;
    }
    await _doSendBroadcast(true);
  };

  const _doSendBroadcast = async (isTest) => {
    setMkSending(true);
    try {
      const fullHtml = rebuildWrappedHtml(mkBodyHtml);
      const res = await fetch(`${API}/admin/marketing/broadcast?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: mkSubject,
          html_body: fullHtml,
          segment: mkSegment,
          test_email: isTest ? mkTestEmail.trim().toLowerCase() : null
        })
      });
      const data = await res.json();
      if (data.success) {
        if (isTest) {
          if (data.partial_failure) {
            toast.warning(`Test failed (${data.last_error || 'unknown'})`);
          } else {
            toast.success(`Test email sent to ${mkTestEmail}`);
          }
        } else {
          if (data.partial_failure) {
            toast.warning(`Sent ${data.sent_count}/${data.recipients_count}. ${data.failed_count} failed (${data.last_error || ''})`);
          } else {
            toast.success(`Broadcast sent! ${data.sent_count}/${data.recipients_count} delivered`);
          }
          setMkSubject('');
          setMkBodyHtml('');
          setMkWrappedHtml('');
          setMkDescription('');
          setMkKeywords('');
          await fetchMarketingBroadcasts();
        }
      } else {
        toast.error(data.message || 'Failed to send broadcast');
      }
    } catch (err) {
      toast.error('Failed to send broadcast');
    } finally {
      setMkSending(false);
      setMkConfirmSend(false);
    }
  };

  const deleteBroadcastLog = async (broadcastId) => {
    setMkConfirmDeleteBroadcast(broadcastId);
  };

  const _doDeleteBroadcastLog = async () => {
    const broadcastId = mkConfirmDeleteBroadcast;
    if (!broadcastId) return;
    try {
      const res = await fetch(`${API}/admin/marketing/broadcasts/${broadcastId}?token=${localStorage.getItem('adminToken')}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Broadcast log deleted');
        await fetchMarketingBroadcasts();
      } else {
        toast.error('Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setMkConfirmDeleteBroadcast(null);
    }
  };

  const fetchMarketingFavorites = async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/favorites?token=${localStorage.getItem('adminToken')}`);
      const data = await res.json();
      if (data.success) setMkFavorites(data.favorites || []);
    } catch (err) {
      console.error('Failed to load favorites', err);
    }
  };

  const saveMarketingFavorite = () => {
    if (!mkSubject.trim() || !mkBodyHtml.trim()) {
      toast.error('Generate or write a draft before saving');
      return;
    }
    // Open name modal instead of window.prompt (blocked in iframes)
    setMkFavName(mkSubject.slice(0, 60));
    setMkFavNameModal(true);
  };

  const _doSaveFavorite = async () => {
    const name = mkFavName.trim();
    if (!name) {
      toast.error('Please enter a name');
      return;
    }
    setMkSavingFav(true);
    try {
      const res = await fetch(`${API}/admin/marketing/favorites?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          subject: mkSubject,
          body_html: mkBodyHtml,
          cta_url: mkCtaUrl,
          cta_label: mkCtaLabel,
          preview_text: mkPreviewText
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Saved to favorites!');
        await fetchMarketingFavorites();
        setMkShowFavorites(true);
        setMkFavNameModal(false);
        setMkFavName('');
      } else {
        toast.error(data.message || 'Failed to save favorite');
      }
    } catch (err) {
      toast.error('Failed to save favorite');
    } finally {
      setMkSavingFav(false);
    }
  };

  const loadMarketingFavorite = (fav) => {
    setMkSubject(fav.subject || '');
    setMkBodyHtml(fav.body_html || '');
    setMkPreviewText(fav.preview_text || '');
    setMkCtaUrl(fav.cta_url || '');
    setMkCtaLabel(fav.cta_label || '');
    toast.success(`Loaded "${fav.name}". Pick audience and send.`);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="mk-subject-input"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const deleteMarketingFavorite = (favId) => {
    setMkConfirmDeleteFavorite(favId);
  };

  const _doDeleteFavorite = async () => {
    const favId = mkConfirmDeleteFavorite;
    if (!favId) return;
    try {
      const res = await fetch(`${API}/admin/marketing/favorites/${favId}?token=${localStorage.getItem('adminToken')}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Favorite deleted');
        await fetchMarketingFavorites();
      } else {
        toast.error('Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setMkConfirmDeleteFavorite(null);
    }
  };

  // ============ Trader Scorecard Admin Functions ============
  const fetchScorecardUsers = async () => {
    setScLoadingUsers(true);
    try {
      const res = await fetch(`${API}/admin/scorecard/users?token=${localStorage.getItem('adminToken')}`);
      const data = await res.json();
      if (data.success) setScUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load scorecard users', err);
    } finally {
      setScLoadingUsers(false);
    }
  };

  const fetchScorecardUserSessions = async (email) => {
    setScSelectedUser(email);
    setScLoadingSessions(true);
    setScSessions([]);
    try {
      const res = await fetch(`${API}/admin/scorecard/user/${encodeURIComponent(email)}/sessions?token=${localStorage.getItem('adminToken')}&limit=100`);
      const data = await res.json();
      if (data.success) setScSessions(data.sessions || []);
    } catch (err) {
      toast.error('Failed to load sessions');
    } finally {
      setScLoadingSessions(false);
    }
  };

  // Support Agents Functions
  const fetchSupportAgents = async () => {
    setLoadingSupportAgents(true);
    try {
      const response = await fetch(`${API}/admin/support-agents?token=${localStorage.getItem('adminToken')}`);
      const data = await response.json();
      if (data.success) {
        setSupportAgents(data.agents || []);
      }
    } catch (err) {
      console.error('Failed to fetch support agents:', err);
    } finally {
      setLoadingSupportAgents(false);
    }
  };

  const createSupportAgent = async () => {
    if (!newAgentForm.username || !newAgentForm.password || !newAgentForm.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreatingAgent(true);
    try {
      const response = await fetch(`${API}/admin/support-agents/create?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAgentForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setNewAgentForm({ username: '', password: '', name: '', email: '' });
        fetchSupportAgents();
      } else {
        toast.error(data.message || 'Failed to create agent');
      }
    } catch (err) {
      toast.error('Failed to create support agent');
    } finally {
      setCreatingAgent(false);
    }
  };

  const deleteSupportAgent = async (username) => {
    if (!window.confirm(`Are you sure you want to delete support agent "${username}"?`)) return;
    try {
      const response = await fetch(`${API}/admin/support-agents/${username}?token=${localStorage.getItem('adminToken')}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Agent deleted');
        fetchSupportAgents();
      } else {
        toast.error(data.message || 'Failed to delete agent');
      }
    } catch (err) {
      toast.error('Failed to delete agent');
    }
  };

  // Subscription Calendar Functions
  const fetchSubscriptionCalendar = async () => {
    setLoadingSubscriptions(true);
    try {
      const response = await fetch(`${API}/admin/subscription/calendar?token=${localStorage.getItem('adminToken')}`);
      const data = await response.json();
      
      if (data.success) {
        setSubscriptionCalendar(data.subscriptions || []);
        setSubscriptionSummary(data.summary || { total: 0, expired: 0, expiring_soon: 0, warning: 0, active: 0 });
      } else {
        toast.error(data.message || 'Failed to fetch subscriptions');
      }
    } catch (err) {
      console.error('Failed to fetch subscription calendar:', err);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const setSubscriptionExpiry = async (guestId, expiryDate, planName = null) => {
    setSettingExpiry(true);
    try {
      const response = await fetch(`${API}/admin/subscription/set-expiry?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          guest_id: guestId, 
          expiry_date: expiryDate,
          plan_name: planName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Expiry date set for ${data.user?.email || 'user'}`);
        setSelectedUserForExpiry(null);
        setNewExpiryDate('');
        await fetchSubscriptionCalendar();
      } else {
        toast.error(data.message || 'Failed to set expiry date');
      }
    } catch (err) {
      toast.error('Failed to set subscription expiry');
    } finally {
      setSettingExpiry(false);
    }
  };

  const sendSubscriptionReminder = async (guestId) => {
    try {
      const response = await fetch(`${API}/admin/subscription/send-reminder?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Reminder email sent!');
        await fetchSubscriptionCalendar();
      } else {
        toast.error(data.message || 'Failed to send reminder');
      }
    } catch (err) {
      toast.error('Failed to send reminder');
    }
  };

  const toggleLifetimeAccess = async (guestId, currentStatus) => {
    const action = currentStatus ? 'revoke' : 'grant';
    if (!window.confirm(`Are you sure you want to ${action} lifetime access for this user?`)) return;
    
    try {
      const response = await fetch(`${API}/admin/subscription/toggle-lifetime?token=${localStorage.getItem('adminToken')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        await fetchSubscriptionCalendar();
      } else {
        toast.error(data.message || 'Failed to toggle lifetime access');
      }
    } catch (err) {
      toast.error('Failed to toggle lifetime access');
    }
  };

  // Toggle Pro Access for a user
  const toggleProAccess = async (email, currentStatus) => {
    const action = currentStatus ? 'revoke' : 'grant';
    
    try {
      const endpoint = currentStatus ? '/admin/revoke-pro-upgrade' : '/admin/approve-pro-upgrade';
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`PRO access ${action}ed for ${email}!`);
        await fetchSubscriptionCalendar();
        await fetchGuests();
      } else {
        toast.error(data.message || `Failed to ${action} PRO access`);
      }
    } catch (err) {
      toast.error(`Failed to ${action} PRO access`);
    }
  };

  // Filter subscriptions
  const filteredSubscriptions = subscriptionCalendar.filter(sub => {
    if (subscriptionFilter === 'all') return true;
    if (subscriptionFilter === 'lifetime') return sub.status === 'lifetime';
    if (subscriptionFilter === 'expired') return sub.status === 'expired';
    if (subscriptionFilter === 'expiring_soon') return sub.status === 'expiring_soon' || sub.status === 'warning';
    if (subscriptionFilter === 'active') return sub.status === 'active';
    if (subscriptionFilter === 'no_expiry') return sub.status === 'no_expiry';
    return true;
  });

  const fetchPaymentProofs = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/payment-proofs?token=${adminToken}`);
      const data = await response.json();
      
      if (data.success) {
        setPaymentProofs(data.proofs || []);
        setUnreadProofs(data.unread || 0);
      }
    } catch (err) {
      // Ignore payment proof fetch errors
    }
  };

  const fetchPendingCodes = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    if (!adminToken) return;
    setLoadingPendingCodes(true);
    try {
      const response = await fetch(`${API}/admin/pending-codes?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setPendingCodes({
          verification_codes: data.verification_codes || [],
          reset_codes: data.reset_codes || [],
          counts: data.counts || { verification_active: 0, reset_active: 0 },
        });
      }
    } catch (err) {
      // ignore
    } finally {
      setLoadingPendingCodes(false);
    }
  };

  const copyCodeToClipboard = async (code, email) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code ${code} copied for ${email}`);
    } catch (e) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success(`Code ${code} copied`); } catch { toast.error('Copy failed'); }
      ta.remove();
    }
  };

  const markProofViewed = async (proofId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      await fetch(`${API}/admin/payment-proofs/${proofId}/mark-viewed?token=${adminToken}`, {
        method: 'POST'
      });
      await fetchPaymentProofs();
    } catch (err) {
      // Ignore
    }
  };

  const verifyPaymentProof = async (proofId, action) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/payment-proofs/${proofId}/verify?token=${adminToken}&action=${action}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(action === 'verify' ? 'Payment verified! User activated.' : 'Payment rejected');
        setSelectedProof(null);
        await fetchPaymentProofs();
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to process');
      }
    } catch (err) {
      toast.error('Failed to process payment proof');
    }
  };

  const deletePaymentProof = async (proofId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/payment-proofs/${proofId}?token=${adminToken}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Payment proof deleted');
        setSelectedProof(null);
        await fetchPaymentProofs();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const fetchReferralCodes = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/referrals?token=${adminToken}`);
      const data = await response.json();
      
      if (data.success) {
        setReferralCodes(data.referrals || []);
      }
    } catch (err) {
      // Ignore referral fetch errors
    }
  };

  // Education Content Functions
  const fetchEducationContent = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/education?token=${adminToken}`);
      const data = await response.json();
      
      if (data.success) {
        setEducationContent(data.content || []);
      }
    } catch (err) {
      // Ignore errors
    }
  };

  // Community Chat Management Functions
  const fetchChatMessages = async () => {
    const adminToken = localStorage.getItem('adminToken');
    setLoadingChat(true);
    try {
      const response = await fetch(`${API}/education/community-chat/comments?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setChatMessages(data.comments || []);
      }
    } catch (err) {
      console.error('Failed to fetch chat messages:', err);
    } finally {
      setLoadingChat(false);
    }
  };

  const fetchSuspendedUsers = async () => {
    const adminToken = localStorage.getItem('adminToken');
    setLoadingSuspended(true);
    try {
      const response = await fetch(`${API}/education/suspended-users?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setSuspendedUsers(data.suspended_users || []);
      }
    } catch (err) {
      console.error('Failed to fetch suspended users:', err);
    } finally {
      setLoadingSuspended(false);
    }
  };

  const handleDeleteChatMessage = async (commentId) => {
    if (!window.confirm('Delete this message?')) return;
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/education/comments/${commentId}?token=${adminToken}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Message deleted');
        fetchChatMessages();
      } else {
        toast.error(data.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const handleSuspendUser = async (email, userName) => {
    if (!window.confirm(`Suspend ${userName} (${email}) from chat?`)) return;
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/education/suspend-user/${encodeURIComponent(email)}?token=${adminToken}&reason=Community guideline violation`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${userName} has been suspended from chat`);
        fetchSuspendedUsers();
        fetchChatMessages();
      } else {
        toast.error(data.message || 'Failed to suspend user');
      }
    } catch (err) {
      toast.error('Failed to suspend user');
    }
  };

  const handleUnsuspendUser = async (email) => {
    if (!window.confirm(`Unsuspend ${email} from chat?`)) return;
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/education/unsuspend-user/${encodeURIComponent(email)}?token=${adminToken}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${email} has been unsuspended`);
        fetchSuspendedUsers();
      } else {
        toast.error(data.message || 'Failed to unsuspend');
      }
    } catch (err) {
      toast.error('Failed to unsuspend user');
    }
  };

  // Trade History Functions
  const fetchTradeHistory = async (token = null, filters = tradeFilters, offset = 0) => {
    const adminToken = token || localStorage.getItem('adminToken');
    setLoadingTrades(true);
    try {
      const params = new URLSearchParams({
        token: adminToken,
        limit: 50,
        offset: offset.toString()
      });
      
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.symbol) params.append('symbol', filters.symbol);
      if (filters.result) params.append('result', filters.result);
      
      const response = await fetch(`${API}/admin/trade-history?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setTradeHistory(data.trades || []);
        setTradePagination(data.pagination || { total: 0, limit: 50, offset: 0, has_more: false });
      }
    } catch (err) {
      console.error('Failed to fetch trade history:', err);
    } finally {
      setLoadingTrades(false);
    }
  };

  const fetchTradeStats = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/trade-stats?token=${adminToken}`);
      const data = await response.json();
      
      if (data.success) {
        setTradeStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch trade stats:', err);
    }
  };

  const handleTradeFilterChange = (key, value) => {
    const newFilters = { ...tradeFilters, [key]: value };
    setTradeFilters(newFilters);
    fetchTradeHistory(null, newFilters, 0);
  };

  const clearTradeFilters = () => {
    const emptyFilters = { user_email: '', symbol: '', result: '' };
    setTradeFilters(emptyFilters);
    fetchTradeHistory(null, emptyFilters, 0);
  };

  const loadMoreTrades = () => {
    const newOffset = tradePagination.offset + tradePagination.limit;
    fetchTradeHistory(null, tradeFilters, newOffset);
  };

  const createEducationContent = async () => {
    if (!newEduTitle.trim() || !newEduUrl.trim()) {
      toast.error('Please enter title and video URL');
      return;
    }
    
    // Validate URL
    if (!newEduUrl.includes('youtube.com') && !newEduUrl.includes('youtu.be') && !newEduUrl.includes('vimeo.com')) {
      toast.error('Only YouTube and Vimeo URLs are supported');
      return;
    }
    
    setSavingEdu(true);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const targetUsers = newEduTargetType === 'specific' 
        ? newEduTargetUsers.split(',').map(e => e.trim()).filter(e => e)
        : [];
      
      const response = await fetch(`${API}/admin/education?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newEduTitle,
          description: newEduDescription,
          video_url: newEduUrl,
          target_type: newEduTargetType,
          target_users: targetUsers
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Education content created!');
        setNewEduTitle('');
        setNewEduDescription('');
        setNewEduUrl('');
        setNewEduTargetType('all');
        setNewEduTargetUsers('');
        await fetchEducationContent();
      } else {
        toast.error(data.message || 'Failed to create content');
      }
    } catch (err) {
      toast.error('Failed to create content');
    } finally {
      setSavingEdu(false);
    }
  };

  const deleteEducationContent = async (contentId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/education/${contentId}?token=${adminToken}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Content deleted');
        await fetchEducationContent();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Toggle archive status for education content
  const toggleArchiveEducation = async (contentId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/education/${contentId}/archive?token=${adminToken}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        await fetchEducationContent();
      } else {
        toast.error(data.message || 'Failed to toggle archive');
      }
    } catch (err) {
      toast.error('Failed to toggle archive');
    }
  };

  // Extend education content duration
  const extendEducationContent = async (contentId, hours = 72) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/education/${contentId}/extend?token=${adminToken}&hours=${hours}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        await fetchEducationContent();
      } else {
        toast.error(data.message || 'Failed to extend duration');
      }
    } catch (err) {
      toast.error('Failed to extend duration');
    }
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return 'Never';
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const createReferralCode = async () => {
    if (!newRefCode.trim() || !newRefName.trim()) {
      toast.error('Please enter code and referrer name');
      return;
    }
    
    setSavingRef(true);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/referrals?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: newRefCode.toUpperCase(),
          discount_percent: newRefDiscount,
          referrer_name: newRefName,
          description: newRefDesc,
          max_uses: 0
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Referral code '${newRefCode.toUpperCase()}' created!`);
        setNewRefCode('');
        setNewRefName('');
        setNewRefDesc('');
        setNewRefDiscount(10);
        await fetchReferralCodes();
      } else {
        toast.error(data.message || 'Failed to create code');
      }
    } catch (err) {
      toast.error('Failed to create referral code');
    } finally {
      setSavingRef(false);
    }
  };

  const toggleReferralCode = async (code, isActive) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/referrals/${code}?token=${adminToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !isActive })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Code ${!isActive ? 'activated' : 'deactivated'}`);
        await fetchReferralCodes();
      }
    } catch (err) {
      toast.error('Failed to update code');
    }
  };

  const deleteReferralCode = async (code) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/referrals/${code}?token=${adminToken}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Code '${code}' deleted`);
        await fetchReferralCodes();
      }
    } catch (err) {
      toast.error('Failed to delete code');
    }
  };

  // Toggle force payment for a user
  const toggleForcePayment = async (guestId, currentStatus) => {
    const adminToken = localStorage.getItem('adminToken');
    setProcessing(guestId);
    try {
      const response = await fetch(`${API}/admin/toggle-force-payment?token=${adminToken}&guest_id=${guestId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        // Update local state
        setGuests(guests.map(g => 
          g.id === guestId ? { ...g, force_payment: data.force_payment } : g
        ));
      } else {
        toast.error(data.message || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to toggle force payment');
    } finally {
      setProcessing(null);
    }
  };

  // Force logout user - invalidates their session
  const forceLogoutUser = async (guestId, guestEmail) => {
    const adminToken = localStorage.getItem('adminToken');
    setProcessing(guestId);
    try {
      const response = await fetch(`${API}/admin/force-logout?token=${adminToken}&guest_id=${guestId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        // Update local state to show user as offline
        setGuests(guests.map(g => 
          g.id === guestId ? { ...g, is_online: false } : g
        ));
      } else {
        toast.error(data.message || 'Failed to force logout');
      }
    } catch (err) {
      toast.error('Failed to force logout user');
    } finally {
      setProcessing(null);
    }
  };

  // Toggle subscription type between trial and paid
  const toggleSubscriptionType = async (guestId, currentType) => {
    const adminToken = localStorage.getItem('adminToken');
    setProcessing(guestId);
    try {
      const response = await fetch(`${API}/admin/toggle-subscription-type?token=${adminToken}&guest_id=${guestId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        // Update local state
        setGuests(guests.map(g => 
          g.id === guestId ? { 
            ...g, 
            subscription_type: data.subscription_type,
            payment_status: data.payment_status
          } : g
        ));
      } else {
        toast.error(data.message || 'Failed to toggle subscription');
      }
    } catch (err) {
      toast.error('Failed to toggle subscription type');
    } finally {
      setProcessing(null);
    }
  };

  // Toggle Top Performer status
  const toggleTopPerformer = async (guestId, currentStatus) => {
    const adminToken = localStorage.getItem('adminToken');
    
    // Default values for new top performers
    let winRate = 75;
    let totalTrades = 50;
    
    // If enabling top performer, use default values (admin can adjust in database if needed)
    // No prompt needed - just enable with sensible defaults
    
    setProcessing(guestId);
    try {
      const response = await fetch(`${API}/admin/toggle-top-performer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: adminToken,
          guest_id: guestId,
          win_rate: winRate,
          total_trades: totalTrades
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        // Update local state
        setGuests(guests.map(g => 
          g.id === guestId ? { ...g, is_top_performer: data.is_top_performer } : g
        ));
      } else {
        toast.error(data.message || 'Failed to toggle top performer');
      }
    } catch (err) {
      toast.error('Failed to toggle top performer status');
    } finally {
      setProcessing(null);
    }
  };

  // Request review from user
  const requestReview = async (guestId, guestName) => {
    const adminToken = localStorage.getItem('adminToken');
    setProcessing(guestId);
    try {
      const response = await fetch(`${API}/admin/request-review?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Review request sent to ${guestName}`);
      } else {
        toast.error(data.message || 'Failed to request review');
      }
    } catch (err) {
      toast.error('Failed to request review');
    } finally {
      setProcessing(null);
    }
  };

  // Fetch reviews for management
  const fetchReviews = async () => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/reviews?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setReviews(data.reviews || []);
        setPendingReviewsCount(data.pending_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch reviews');
    }
  };

  // Approve a review
  const approveReview = async (reviewId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/review/approve?token=${adminToken}&review_id=${reviewId}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Review approved and published!');
        fetchReviews();
      } else {
        toast.error(data.message || 'Failed to approve review');
      }
    } catch (err) {
      toast.error('Failed to approve review');
    }
  };

  // Reject a review
  const rejectReview = async (reviewId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/review/reject?token=${adminToken}&review_id=${reviewId}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Review rejected');
        fetchReviews();
      } else {
        toast.error(data.message || 'Failed to reject review');
      }
    } catch (err) {
      toast.error('Failed to reject review');
    }
  };

  // Fetch support tickets
  const fetchSupportTickets = async () => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/support-tickets?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setSupportTickets(data.tickets || []);
        const openCount = (data.tickets || []).filter(t => t.status === 'open').length;
        setOpenTicketsCount(openCount);
      }
    } catch (err) {
      console.error('Failed to fetch support tickets');
    }
  };

  // Close a support ticket
  const closeSupportTicket = async (ticketId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/support-tickets/${ticketId}/close?token=${adminToken}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Ticket closed');
        fetchSupportTickets();
      } else {
        toast.error(data.message || 'Failed to close ticket');
      }
    } catch (err) {
      toast.error('Failed to close ticket');
    }
  };

  // Coaching Management Functions
  const fetchCoachingData = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      // Fetch bookings
      const bookingsRes = await fetch(`${API}/admin/coaching/bookings?token=${adminToken}`);
      const bookingsData = await bookingsRes.json();
      if (bookingsData.success) {
        setCoachingBookings(bookingsData.bookings);
        const pending = bookingsData.bookings.filter(b => b.status === 'payment_submitted').length;
        setPendingCoachingCount(pending);
      }

      // Fetch availability
      const availRes = await fetch(`${API}/admin/coaching/availability?token=${adminToken}&month=${coachingViewMonth + 1}&year=${coachingViewYear}`);
      const availData = await availRes.json();
      if (availData.success) {
        setCoachingAvailability(availData.availability);
      }

      // Fetch tickets
      const ticketsRes = await fetch(`${API}/admin/coaching/tickets?token=${adminToken}`);
      const ticketsData = await ticketsRes.json();
      if (ticketsData.success) {
        setCoachingTickets(ticketsData.tickets);
      }
    } catch (err) {
      console.error('Error fetching coaching data:', err);
    }
  };

  const addCoachingAvailability = async () => {
    if (!newAvailDate || !newAvailTimes) {
      toast.error('Please enter date and time slots');
      return;
    }

    setSavingAvailability(true);
    const adminToken = localStorage.getItem('adminToken');

    try {
      // Parse times (comma or space separated)
      const times = newAvailTimes.split(/[,\s]+/).filter(t => t.match(/^\d{1,2}:\d{2}$/));
      
      if (times.length === 0) {
        toast.error('Please enter valid times (e.g., 10:00, 14:30)');
        setSavingAvailability(false);
        return;
      }

      const response = await fetch(`${API}/admin/coaching/availability?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: newAvailDate,
          time_slots: times
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Added ${data.added} time slots`);
        setNewAvailDate('');
        setNewAvailTimes('');
        fetchCoachingData();
      } else {
        toast.error(data.message || 'Failed to add availability');
      }
    } catch (err) {
      toast.error('Failed to add availability');
    } finally {
      setSavingAvailability(false);
    }
  };

  const deleteCoachingSlot = async (slotId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/coaching/availability?slot_id=${slotId}&token=${adminToken}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Slot removed');
        fetchCoachingData();
      } else {
        toast.error(data.message || 'Failed to remove slot');
      }
    } catch (err) {
      toast.error('Failed to remove slot');
    }
  };

  const confirmCoachingBooking = async (bookingId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/coaching/confirm?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Booking confirmed! Confirmation email sent.');
        fetchCoachingData();
      } else {
        toast.error(data.message || 'Failed to confirm booking');
      }
    } catch (err) {
      toast.error('Failed to confirm booking');
    }
  };

  const cancelCoachingBooking = async (bookingId, reason = '') => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/coaching/cancel?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, reason })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Booking cancelled');
        fetchCoachingData();
      } else {
        toast.error(data.message || 'Failed to cancel booking');
      }
    } catch (err) {
      toast.error('Failed to cancel booking');
    }
  };

  const closeCoachingTicket = async (ticketId) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/coaching/ticket/close?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: ticketId })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Ticket closed');
        fetchCoachingData();
      } else {
        toast.error(data.message || 'Failed to close ticket');
      }
    } catch (err) {
      toast.error('Failed to close ticket');
    }
  };

  // ============ PRODUCT MANAGEMENT FUNCTIONS ============
  const fetchProducts = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/products?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setProducts(data.products || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchSiteContent = async () => {
    try {
      const response = await fetch(`${API}/site-content`);
      const data = await response.json();
      if (data.success && data.content) {
        setSiteContent(data.content);
      }
    } catch (err) {
      console.error('Error fetching site content:', err);
    }
  };

  const saveSiteContent = async () => {
    const adminToken = localStorage.getItem('adminToken');
    setSavingSiteContent(true);
    try {
      const response = await fetch(`${API}/admin/site-content?token=${adminToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: siteContent })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Site content saved successfully!');
      } else {
        toast.error(data.message || 'Failed to save content');
      }
    } catch (err) {
      toast.error('Error saving content');
      console.error('Error saving site content:', err);
    } finally {
      setSavingSiteContent(false);
    }
  };

  const resetProductForm = () => {
    setProductForm({
      product_id: '',
      name: '',
      category: 'signal_generator',
      short_description: '',
      full_description: '',
      image_url: '',
      price: 0,
      price_type: 'monthly',
      secondary_price: '',
      secondary_price_type: '',
      features: [],
      is_active: true,
      display_order: 0,
      sub_products: [],
      payment_link: '',
      pro_upgrade_payment_link: ''
    });
    setNewFeature('');
    setEditingProduct(null);
  };

  const startEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      product_id: product.product_id || '',
      name: product.name || '',
      category: product.category || 'signal_generator',
      short_description: product.short_description || '',
      full_description: product.full_description || '',
      image_url: product.image_url || '',
      price: product.price || 0,
      price_type: product.price_type || 'monthly',
      secondary_price: product.secondary_price || '',
      secondary_price_type: product.secondary_price_type || '',
      features: product.features || [],
      is_active: product.is_active !== false,
      display_order: product.display_order || 0,
      sub_products: product.sub_products || [],
      payment_link: product.payment_link || '',
      pro_upgrade_payment_link: product.pro_upgrade_payment_link || ''
    });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setProductForm(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setProductForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const saveProduct = async () => {
    const adminToken = localStorage.getItem('adminToken');
    if (!productForm.name || !productForm.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setSavingProduct(true);
    try {
      const url = editingProduct
        ? `${API}/admin/products/${editingProduct.product_id}?token=${adminToken}`
        : `${API}/admin/products?token=${adminToken}`;
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(editingProduct ? 'Product updated!' : 'Product created!');
        resetProductForm();
        fetchProducts();
      } else {
        toast.error(data.message || 'Failed to save product');
      }
    } catch (err) {
      toast.error('Failed to save product');
    } finally {
      setSavingProduct(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/products/${productId}?token=${adminToken}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Product deleted');
        fetchProducts();
      } else {
        toast.error(data.message || 'Failed to delete product');
      }
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const toggleProductActive = async (product) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/products/${product.product_id}?token=${adminToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !product.is_active })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(product.is_active ? 'Product deactivated' : 'Product activated');
        fetchProducts();
      } else {
        toast.error(data.message || 'Failed to update product');
      }
    } catch (err) {
      toast.error('Failed to update product');
    }
  };

  const fetchPromotion = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/promotion?token=${adminToken}`);
      const data = await response.json();
      
      if (data.success) {
        setPromoEnabled(data.enabled || false);
        setPromoCode(data.discount_code || '');
        setPromoText(data.discount_text || '');
        setPromoColor(data.banner_color || 'yellow');
        setPromoDiscount(data.discount_percent || 10);
        setPromoEndTime(data.end_time || '');
      }
    } catch (err) {
      // Ignore promotion fetch errors
    }
  };

  // Fetch global ad settings
  const fetchAdSettings = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/settings/ads?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setAdsEnabled(data.ads_enabled);
      }
    } catch (err) {
      // Ignore errors
    }
  };

  // Toggle global ads on/off
  const toggleGlobalAds = async () => {
    setTogglingAds(true);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/settings/ads/toggle?token=${adminToken}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAdsEnabled(data.ads_enabled);
        toast.success(data.message);
      } else {
        toast.error(data.message || 'Failed to toggle ads');
      }
    } catch (err) {
      toast.error('Failed to toggle ad settings');
    } finally {
      setTogglingAds(false);
    }
  };

  // Fetch market alert settings
  const fetchMarketAlertSettings = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/settings/market-alert?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setMarketAlertEnabled(data.alert_enabled);
      }
    } catch (err) {
      // Ignore errors
    }
  };

  // Toggle market manipulation alert on/off
  const toggleMarketAlert = async () => {
    setTogglingMarketAlert(true);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/settings/market-alert/toggle?token=${adminToken}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMarketAlertEnabled(data.alert_enabled);
        if (data.alert_enabled) {
          toast.error(
            <div>
              <p className="font-bold">🚨 Market Alert ACTIVATED</p>
              <p className="text-sm">Users will see a warning when trying to trade</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.success('Market alert deactivated');
        }
      } else {
        toast.error(data.message || 'Failed to toggle market alert');
      }
    } catch (err) {
      toast.error('Failed to toggle market alert');
    } finally {
      setTogglingMarketAlert(false);
    }
  };

  // Fetch PWA version info
  const fetchAppVersion = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/app/version?token=${adminToken}`);
      const data = await response.json();
      if (data.success) {
        setAppVersion(data.version || '1.0.0');
        setLastUpdatePushed(data.last_pushed_at);
      }
    } catch (err) {
      // Ignore errors
    }
  };

  // Push PWA update to all users
  const pushAppUpdate = async () => {
    setPushingUpdate(true);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const params = new URLSearchParams({ token: adminToken });
      if (updateMessage) {
        params.append('message', updateMessage);
      }
      
      const response = await fetch(`${API}/admin/app/push-update?${params.toString()}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAppVersion(data.version);
        setLastUpdatePushed(data.build_timestamp);
        setUpdateMessage('');
        toast.success(data.message);
      } else {
        toast.error(data.message || 'Failed to push update');
      }
    } catch (err) {
      toast.error('Failed to push app update');
    } finally {
      setPushingUpdate(false);
    }
  };

  const savePromotion = async () => {
    setSavingPromo(true);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/promotion?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: promoEnabled,
          discount_code: promoCode,
          discount_text: promoText,
          banner_color: promoColor,
          discount_percent: promoDiscount,
          end_time: promoEndTime || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || 'Promotion settings saved!');
      } else {
        toast.error(data.message || 'Failed to save promotion');
      }
    } catch (err) {
      toast.error('Failed to save promotion settings');
    } finally {
      setSavingPromo(false);
    }
  };

  // Quick toggle for promotion banner - saves immediately
  const quickTogglePromotion = async (newEnabled) => {
    setPromoEnabled(newEnabled);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/promotion?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: newEnabled,
          discount_code: promoCode,
          discount_text: promoText,
          banner_color: promoColor,
          discount_percent: promoDiscount
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(newEnabled ? 'Promotion banner enabled!' : 'Promotion banner disabled!');
      } else {
        toast.error(data.message || 'Failed to toggle promotion');
        setPromoEnabled(!newEnabled); // Revert on failure
      }
    } catch (err) {
      toast.error('Failed to toggle promotion');
      setPromoEnabled(!newEnabled); // Revert on failure
    }
  };

  // Grant bonus days to user
  const grantBonusDays = async (userId, days) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/user/${userId}/grant-bonus?token=${adminToken}&days=${days}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        setShowBonusModal(null);
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to grant bonus days');
      }
    } catch (err) {
      toast.error('Failed to grant bonus days');
    }
  };

  // Set/override extension days
  const setExtensionDays = async (userId, days) => {
    const adminToken = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/user/${userId}/set-extension?token=${adminToken}&days=${days}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        setShowBonusModal(null);
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to set extension');
      }
    } catch (err) {
      toast.error('Failed to set extension');
    }
  };

  const fetchGuests = async (token = null) => {
    const adminToken = token || localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API}/admin/guests?token=${adminToken}`);
      const data = await response.json();
      
      if (data.success) {
        setGuests(data.guests || []);
        setSummary(data.summary || {
          total: 0,
          pending_verification: 0,
          pending_approval: 0,
          approved: 0,
          rejected: 0,
          blocked: 0,
          online: 0,
          trial_users: 0,
          paid_users: 0,
          trial_expired: 0
        });
        setOnlineUsers(data.online_users || []);
      } else {
        toast.error(data.message || 'Failed to fetch guests');
      }
    } catch (err) {
      toast.error('Failed to load guest data');
    }
  };

  const handleApproval = async (guestId, action) => {
    setProcessing(guestId);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/guests/approve?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId, action })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Guest ${action}d successfully!`);
        await fetchGuests();
      } else {
        toast.error(data.message || `Failed to ${action} guest`);
      }
    } catch (err) {
      toast.error(`Error ${action}ing guest`);
    } finally {
      setProcessing(null);
    }
  };

  const handleVerifyEmail = async (guestId) => {
    setProcessing(guestId);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/guests/verify-email?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId, action: 'verify' })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Email verified! Guest moved to pending approval.');
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to verify email');
      }
    } catch (err) {
      toast.error('Error verifying email');
    } finally {
      setProcessing(null);
    }
  };

  const handleBlock = async (guestId, blockType) => {
    setProcessing(guestId);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/guests/block?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId, block_type: blockType })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Guest ${blockType}ly blocked!`);
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to block guest');
      }
    } catch (err) {
      toast.error('Error blocking guest');
    } finally {
      setProcessing(null);
    }
  };

  const handleUnblock = async (guestId) => {
    setProcessing(guestId);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/guests/unblock?guest_id=${guestId}&token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Guest unblocked!');
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to unblock guest');
      }
    } catch (err) {
      toast.error('Error unblocking guest');
    } finally {
      setProcessing(null);
    }
  };

  // Pro Access Management
  const handleGrantProAccess = async (guestEmail) => {
    if (!window.confirm(`Grant PRO access to ${guestEmail}?\n\nThis will give them access to the Binary Scalper Bot.`)) {
      return;
    }
    
    setProcessing(guestEmail);
    
    try {
      const response = await fetch(`${API}/admin/approve-pro-upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: guestEmail })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`PRO access granted to ${guestEmail}!`);
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to grant PRO access');
      }
    } catch (err) {
      toast.error('Error granting PRO access');
    } finally {
      setProcessing(null);
    }
  };

  const handleRevokeProAccess = async (guestEmail) => {
    if (!window.confirm(`Revoke PRO access from ${guestEmail}?\n\nThey will lose access to the Binary Scalper Bot.`)) {
      return;
    }
    
    setProcessing(guestEmail);
    
    try {
      const response = await fetch(`${API}/admin/revoke-pro-upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: guestEmail })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`PRO access revoked from ${guestEmail}`);
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to revoke PRO access');
      }
    } catch (err) {
      toast.error('Error revoking PRO access');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteGuest = async (guestId, guestEmail) => {
    if (!window.confirm(`Are you sure you want to DELETE the account for ${guestEmail}?\n\nThis action cannot be undone. The user will need to sign up again.`)) {
      return;
    }
    
    setProcessing(guestId);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/guests/${guestId}?token=${adminToken}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Account ${guestEmail} deleted successfully`);
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to delete account');
      }
    } catch (err) {
      toast.error('Error deleting account');
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkAsPaid = async (guestId) => {
    setProcessing(guestId);
    const adminToken = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API}/admin/guests/mark-paid?token=${adminToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Payment confirmed! User subscription activated.');
        await fetchGuests();
      } else {
        toast.error(data.message || 'Failed to activate subscription');
      }
    } catch (err) {
      toast.error('Error activating subscription');
    } finally {
      setProcessing(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const getStatusBadge = (status, isBlocked, subscriptionType) => {
    if (isBlocked) {
      return <Badge variant="outline" className="bg-orange-500/20 text-orange-400 border-orange-500/30"><Ban className="w-3 h-3 mr-1" />Blocked</Badge>;
    }
    switch (status) {
      case 'pending_verification':
        return <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Mail className="w-3 h-3 mr-1" />Pending Email</Badge>;
      case 'pending_approval':
        return <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Clock className="w-3 h-3 mr-1" />Pending Payment</Badge>;
      case 'trial':
        return <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30"><Zap className="w-3 h-3 mr-1" />Trial Active</Badge>;
      case 'trial_expired':
        return <Badge variant="outline" className="bg-orange-500/20 text-orange-400 border-orange-500/30"><Clock className="w-3 h-3 mr-1" />Trial Expired</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Subscribed</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30"><X className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSubscriptionBadge = (subscriptionType, paymentStatus) => {
    if (subscriptionType === 'trial') {
      return <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30"><Zap className="w-3 h-3 mr-1" />Free Trial</Badge>;
    }
    if (paymentStatus === 'paid') {
      return <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30"><DollarSign className="w-3 h-3 mr-1" />Paid</Badge>;
    }
    if (paymentStatus === 'pending') {
      return <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Payment Pending</Badge>;
    }
    return <Badge variant="outline" className="bg-gray-500/20 text-gray-400 border-gray-500/30"><CreditCard className="w-3 h-3 mr-1" />Unpaid</Badge>;
  };

  const formatTrialExpiry = (trialExpires) => {
    if (!trialExpires) return null;
    try {
      const expiry = new Date(trialExpires);
      const now = new Date();
      const hoursLeft = Math.max(0, Math.round((expiry - now) / (1000 * 60 * 60)));
      if (hoursLeft <= 0) return 'Expired';
      if (hoursLeft < 24) return `${hoursLeft}h left`;
      return `${Math.round(hoursLeft / 24)}d left`;
    } catch {
      return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const filterGuestsByStatus = (status) => {
    let filtered = guests.filter(g => g.status === status);
    
    // For approved users, sort by: online first, then by last activity (most recent first)
    if (status === 'approved') {
      filtered.sort((a, b) => {
        // Online users first
        if (a.is_online && !b.is_online) return -1;
        if (!a.is_online && b.is_online) return 1;
        
        // Then by last activity (most recent first)
        const aTime = a.last_activity || a.last_login || '';
        const bTime = b.last_activity || b.last_login || '';
        return bTime.localeCompare(aTime);
      });
    }
    
    // For pending_verification, sort by created_at (newest first)
    if (status === 'pending_verification') {
      filtered.sort((a, b) => {
        const aTime = a.created_at || '';
        const bTime = b.created_at || '';
        return bTime.localeCompare(aTime);
      });
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <div className="flex items-center gap-2">
                <p className="text-gray-400">Welcome, {admin?.username}</p>
                <Badge className={adminRole === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}>
                  {adminRole === 'admin' ? 'Admin' : 'Moderator'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Chat Bubble - Opens messenger with specific user */}
            <AdminChatBubble 
              onSelectUser={(user) => {
                setMessengerUser(user);
                setShowMessenger(true);
              }}
              onNewMessage={(user) => {
                // Auto-open chat when new message arrives
                setMessengerUser(user);
                setShowMessenger(true);
              }}
            />
            <Button
              variant="outline"
              onClick={() => navigate('/admin/courses')}
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="admin-nav-courses"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Courses
            </Button>
            <Button
              variant="outline"
              onClick={() => fetchGuests()}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Live Chat Support Panel */}
      <div className="max-w-6xl mx-auto mb-8">
        <AdminLiveChatPanel token={localStorage.getItem('adminToken')} API={API} />
      </div>

      {/* Staff Management Panel - Admin Only */}
      {isAdmin() && (
        <div className="max-w-6xl mx-auto mb-8">
          <Card className="bg-slate-800/50 border-white/10">
            <CardHeader 
              className="cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setShowStaffPanel(!showStaffPanel)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users2 className="w-5 h-5 text-purple-400" />
                  <CardTitle className="text-white">Staff Management</CardTitle>
                  <Badge variant="outline" className="border-purple-400 text-purple-400">
                    {staffList.length} Staff
                  </Badge>
                </div>
                {showStaffPanel ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </div>
            </CardHeader>
            
            {showStaffPanel && (
              <CardContent className="space-y-6">
                {/* Invite New Staff */}
                <div className="bg-slate-900/50 rounded-lg p-4 border border-white/10">
                  <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-green-400" />
                    Invite New Staff Member
                  </h3>
                  <div className="flex flex-col md:flex-row gap-3">
                    <Input
                      type="email"
                      placeholder="staff@email.com"
                      value={newStaffEmail}
                      onChange={(e) => setNewStaffEmail(e.target.value)}
                      className="flex-1 bg-slate-800 border-white/10 text-white"
                    />
                    <Select value={newStaffRole} onValueChange={setNewStaffRole}>
                      <SelectTrigger className="w-[150px] bg-slate-800 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="moderator">Moderator</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={inviteStaff}
                      disabled={invitingStaff}
                      className="bg-purple-600 hover:bg-purple-500"
                    >
                      {invitingStaff ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Invite
                    </Button>
                  </div>
                </div>

                {/* Role Permissions Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <h4 className="text-purple-400 font-medium mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin Permissions
                    </h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>✓ Full access to all features</li>
                      <li>✓ Can add/remove staff members</li>
                      <li>✓ Can modify all settings</li>
                      <li>✓ Can manage products & content</li>
                    </ul>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Moderator Permissions
                    </h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>✓ Verify & approve payments</li>
                      <li>✓ Answer support tickets</li>
                      <li>✓ Pause/unpause users</li>
                      <li>✗ Cannot modify settings or content</li>
                      <li>✗ Cannot delete staff members</li>
                    </ul>
                  </div>
                </div>

                {/* Staff List */}
                <div>
                  <h3 className="text-white font-medium mb-3">Current Staff Members</h3>
                  {staffList.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No staff members added yet</p>
                  ) : (
                    <div className="space-y-2">
                      {staffList.map((staff) => (
                        <div 
                          key={staff.email} 
                          className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3 border border-white/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              staff.role === 'admin' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                            }`}>
                              {staff.role === 'admin' ? (
                                <Shield className="w-5 h-5 text-purple-400" />
                              ) : (
                                <User className="w-5 h-5 text-blue-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{staff.username || staff.email.split('@')[0]}</p>
                              <p className="text-sm text-gray-400">{staff.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={staff.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}>
                              {staff.role}
                            </Badge>
                            <Badge variant={staff.is_active !== false ? 'default' : 'secondary'}>
                              {staff.is_active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteStaff(staff.email)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Support Agents Management Panel - Admin Only */}
      {isAdmin() && (
        <div className="max-w-6xl mx-auto mb-8">
          <Card className="bg-slate-800/50 border-white/10">
            <CardHeader 
              className="cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => {
                setShowSupportAgentsPanel(!showSupportAgentsPanel);
                if (!showSupportAgentsPanel) fetchSupportAgents();
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                  <CardTitle className="text-white">Support Agents</CardTitle>
                  <Badge className="bg-green-500/20 text-green-400">Customer Support</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-gray-400">
                    {supportAgents.length} agents
                  </Badge>
                  {showSupportAgentsPanel ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>
              <CardDescription className="text-gray-400">
                Create and manage customer support agents. They can only access the Live Chat.
              </CardDescription>
            </CardHeader>
            {showSupportAgentsPanel && (
              <CardContent className="border-t border-white/10">
                <div className="space-y-4">
                  {/* Create New Agent Form */}
                  <div className="p-4 bg-slate-900/50 rounded-xl border border-white/10">
                    <h4 className="text-white font-medium mb-3">Create New Support Agent</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <Input
                        placeholder="Username *"
                        value={newAgentForm.username}
                        onChange={(e) => setNewAgentForm({...newAgentForm, username: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                      <Input
                        type="password"
                        placeholder="Password *"
                        value={newAgentForm.password}
                        onChange={(e) => setNewAgentForm({...newAgentForm, password: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                      <Input
                        placeholder="Display Name *"
                        value={newAgentForm.name}
                        onChange={(e) => setNewAgentForm({...newAgentForm, name: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                      <Input
                        type="email"
                        placeholder="Email (optional)"
                        value={newAgentForm.email}
                        onChange={(e) => setNewAgentForm({...newAgentForm, email: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <Button
                      onClick={createSupportAgent}
                      disabled={creatingAgent}
                      className="mt-3 bg-green-600 hover:bg-green-700"
                    >
                      {creatingAgent ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Create Agent
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      Support agents can only access the chat panel at <strong>/support</strong>
                    </p>
                  </div>

                  {/* Agents List */}
                  {loadingSupportAgents ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                    </div>
                  ) : supportAgents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No support agents created yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {supportAgents.map((agent) => (
                        <div 
                          key={agent.username} 
                          className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3 border border-white/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${agent.is_live ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                              <MessageCircle className={`w-5 h-5 ${agent.is_live ? 'text-green-400' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{agent.name}</p>
                                <span className={`w-2 h-2 rounded-full ${agent.is_live ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                              </div>
                              <p className="text-sm text-gray-400">@{agent.username}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {agent.today_duration_formatted && (
                              <Badge variant="outline" className="text-gray-400">
                                <Clock className="w-3 h-3 mr-1" />
                                Today: {agent.today_duration_formatted}
                              </Badge>
                            )}
                            <Badge className={agent.is_live ? 'bg-green-500' : 'bg-gray-500'}>
                              {agent.is_live ? 'Live' : 'Offline'}
                            </Badge>
                            <Badge variant="outline" className="text-blue-400">
                              {agent.total_chats_handled || 0} chats
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSupportAgent(agent.username)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Subscription Calendar Panel - Admin Only */}
      {isAdmin() && (
        <div className="max-w-6xl mx-auto mb-8">
          <Card className="bg-slate-800/50 border-white/10">
            <CardHeader 
              className="cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setShowSubscriptionCalendar(!showSubscriptionCalendar)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-amber-400" />
                  <CardTitle className="text-white">Subscription Calendar</CardTitle>
                  {subscriptionSummary.expired > 0 && (
                    <Badge className="bg-red-500 text-white">
                      {subscriptionSummary.expired} Expired
                    </Badge>
                  )}
                  {subscriptionSummary.expiring_soon > 0 && (
                    <Badge className="bg-orange-500 text-white">
                      {subscriptionSummary.expiring_soon} Expiring Soon
                    </Badge>
                  )}
                  <Badge variant="outline" className="border-green-400 text-green-400">
                    {subscriptionSummary.active} Active
                  </Badge>
                </div>
                {showSubscriptionCalendar ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </div>
            </CardHeader>
            
            {showSubscriptionCalendar && (
              <CardContent className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center border border-white/10">
                    <p className="text-2xl font-bold text-white">{subscriptionSummary.total}</p>
                    <p className="text-xs text-gray-400">Total</p>
                  </div>
                  <div className="bg-purple-500/20 rounded-lg p-3 text-center border border-purple-500/30">
                    <p className="text-2xl font-bold text-purple-400">{subscriptionSummary.lifetime || 0}</p>
                    <p className="text-xs text-purple-300">👑 Lifetime</p>
                  </div>
                  <div className="bg-red-500/20 rounded-lg p-3 text-center border border-red-500/30">
                    <p className="text-2xl font-bold text-red-400">{subscriptionSummary.expired}</p>
                    <p className="text-xs text-red-300">Expired</p>
                  </div>
                  <div className="bg-orange-500/20 rounded-lg p-3 text-center border border-orange-500/30">
                    <p className="text-2xl font-bold text-orange-400">{subscriptionSummary.expiring_soon}</p>
                    <p className="text-xs text-orange-300">≤3 Days</p>
                  </div>
                  <div className="bg-yellow-500/20 rounded-lg p-3 text-center border border-yellow-500/30">
                    <p className="text-2xl font-bold text-yellow-400">{subscriptionSummary.warning}</p>
                    <p className="text-xs text-yellow-300">≤7 Days</p>
                  </div>
                  <div className="bg-green-500/20 rounded-lg p-3 text-center border border-green-500/30">
                    <p className="text-2xl font-bold text-green-400">{subscriptionSummary.active}</p>
                    <p className="text-xs text-green-300">Active</p>
                  </div>
                  <div className="bg-gray-500/20 rounded-lg p-3 text-center border border-gray-500/30">
                    <p className="text-2xl font-bold text-gray-400">{subscriptionSummary.no_expiry || 0}</p>
                    <p className="text-xs text-gray-300">No Expiry</p>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={subscriptionFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('all')}
                    className={subscriptionFilter === 'all' ? 'bg-amber-600' : 'border-white/20 text-white'}
                  >
                    All ({subscriptionSummary.total})
                  </Button>
                  <Button
                    size="sm"
                    variant={subscriptionFilter === 'lifetime' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('lifetime')}
                    className={subscriptionFilter === 'lifetime' ? 'bg-purple-600' : 'border-purple-500/50 text-purple-400'}
                  >
                    👑 Lifetime ({subscriptionSummary.lifetime || 0})
                  </Button>
                  <Button
                    size="sm"
                    variant={subscriptionFilter === 'expired' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('expired')}
                    className={subscriptionFilter === 'expired' ? 'bg-red-600' : 'border-red-500/50 text-red-400'}
                  >
                    Expired ({subscriptionSummary.expired})
                  </Button>
                  <Button
                    size="sm"
                    variant={subscriptionFilter === 'expiring_soon' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('expiring_soon')}
                    className={subscriptionFilter === 'expiring_soon' ? 'bg-orange-600' : 'border-orange-500/50 text-orange-400'}
                  >
                    Expiring Soon ({subscriptionSummary.expiring_soon + subscriptionSummary.warning})
                  </Button>
                  <Button
                    size="sm"
                    variant={subscriptionFilter === 'active' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('active')}
                    className={subscriptionFilter === 'active' ? 'bg-green-600' : 'border-green-500/50 text-green-400'}
                  >
                    Active ({subscriptionSummary.active})
                  </Button>
                  <Button
                    size="sm"
                    variant={subscriptionFilter === 'no_expiry' ? 'default' : 'outline'}
                    onClick={() => setSubscriptionFilter('no_expiry')}
                    className={subscriptionFilter === 'no_expiry' ? 'bg-gray-600' : 'border-gray-500/50 text-gray-400'}
                  >
                    No Expiry ({subscriptionSummary.no_expiry || 0})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchSubscriptionCalendar}
                    disabled={loadingSubscriptions}
                    className="border-white/20 text-white ml-auto"
                  >
                    {loadingSubscriptions ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Set Expiry for User Modal */}
                {selectedUserForExpiry && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <h4 className="text-amber-300 font-medium mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Set Expiry Date for: {selectedUserForExpiry.name || selectedUserForExpiry.email}
                    </h4>
                    <div className="flex flex-col md:flex-row gap-3">
                      <input
                        type="date"
                        value={newExpiryDate}
                        onChange={(e) => setNewExpiryDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="flex-1 px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white"
                      />
                      <Button
                        onClick={() => setSubscriptionExpiry(selectedUserForExpiry.id || selectedUserForExpiry.email, newExpiryDate)}
                        disabled={!newExpiryDate || settingExpiry}
                        className="bg-amber-600 hover:bg-amber-500"
                      >
                        {settingExpiry ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                        Set Expiry
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { setSelectedUserForExpiry(null); setNewExpiryDate(''); }}
                        className="border-white/20 text-white"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Subscriptions List */}
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {loadingSubscriptions ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-8 h-8 animate-spin text-amber-400 mx-auto mb-2" />
                      <p className="text-gray-400">Loading subscriptions...</p>
                    </div>
                  ) : filteredSubscriptions.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400">No subscriptions found</p>
                      <p className="text-gray-500 text-sm mt-1">Set expiry dates for users in the Users & Purchases panel</p>
                    </div>
                  ) : (
                    filteredSubscriptions.map((sub, idx) => (
                      <div 
                        key={sub.id || idx}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          sub.status === 'expired' ? 'bg-red-500/10 border-red-500/30' :
                          sub.status === 'expiring_soon' ? 'bg-orange-500/10 border-orange-500/30' :
                          sub.status === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                          'bg-slate-900/50 border-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            sub.status === 'expired' ? 'bg-red-500/20' :
                            sub.status === 'expiring_soon' ? 'bg-orange-500/20' :
                            sub.status === 'warning' ? 'bg-yellow-500/20' :
                            sub.status === 'lifetime' ? 'bg-purple-500/20' :
                            sub.status === 'no_expiry' ? 'bg-gray-500/20' :
                            'bg-green-500/20'
                          }`}>
                            {sub.status === 'lifetime' ? (
                              <span className="text-lg">👑</span>
                            ) : sub.status === 'expired' ? (
                              <X className="w-5 h-5 text-red-400" />
                            ) : sub.status === 'expiring_soon' ? (
                              <AlertCircle className="w-5 h-5 text-orange-400" />
                            ) : sub.status === 'warning' ? (
                              <Clock className="w-5 h-5 text-yellow-400" />
                            ) : sub.status === 'no_expiry' ? (
                              <Clock className="w-5 h-5 text-gray-400" />
                            ) : (
                              <Check className="w-5 h-5 text-green-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">{sub.name}</p>
                              {sub.lifetime_access && (
                                <Badge className="bg-purple-500/30 text-purple-300 text-xs">
                                  👑 LIFETIME
                                </Badge>
                              )}
                              {sub.has_pro_access && (
                                <Badge className="bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-pink-300 text-xs border border-pink-500/30">
                                  ⚡ PRO
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-400">{sub.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className={`font-medium ${
                              sub.status === 'lifetime' ? 'text-purple-400' :
                              sub.status === 'expired' ? 'text-red-400' :
                              sub.status === 'expiring_soon' ? 'text-orange-400' :
                              sub.status === 'warning' ? 'text-yellow-400' :
                              sub.status === 'no_expiry' ? 'text-gray-400' :
                              'text-green-400'
                            }`}>
                              {sub.status === 'lifetime' ? '∞ LIFETIME' :
                               sub.status === 'expired' ? 'EXPIRED' : 
                               sub.status === 'no_expiry' ? 'Not Set' :
                               `${sub.days_until} days`}
                            </p>
                            <p className="text-xs text-gray-500">{sub.expires_formatted}</p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* Lifetime Access Toggle */}
                            <Button
                              size="sm"
                              onClick={() => toggleLifetimeAccess(sub.id || sub.email, sub.lifetime_access)}
                              className={sub.lifetime_access ? 'bg-purple-600 hover:bg-purple-500' : 'bg-gray-600 hover:bg-gray-500'}
                              title={sub.lifetime_access ? 'Revoke lifetime access' : 'Grant lifetime access'}
                            >
                              👑 {sub.lifetime_access ? 'Revoke' : 'Grant'}
                            </Button>
                            
                            {/* Pro Access Toggle */}
                            <Button
                              size="sm"
                              onClick={() => toggleProAccess(sub.email, sub.has_pro_access)}
                              className={sub.has_pro_access ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500' : 'bg-gray-600 hover:bg-gray-500'}
                              title={sub.has_pro_access ? 'Revoke PRO access' : 'Grant PRO access'}
                            >
                              <Zap className="w-3 h-3 mr-1" />
                              {sub.has_pro_access ? 'PRO ✓' : '+PRO'}
                            </Button>
                            
                            {/* Send Reminder Button - only for non-lifetime users */}
                            {!sub.lifetime_access && sub.status !== 'expired' && sub.status !== 'no_expiry' && sub.days_until <= 7 && !sub.reminder_sent && (
                              <Button
                                size="sm"
                                onClick={() => sendSubscriptionReminder(sub.id || sub.email)}
                                className="bg-blue-600 hover:bg-blue-500"
                                title="Send reminder email"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                            {sub.reminder_sent && !sub.lifetime_access && (
                              <Badge variant="outline" className="border-blue-500/50 text-blue-400 text-xs">
                                Reminded
                              </Badge>
                            )}
                            
                            {/* Edit Expiry Button - only for non-lifetime users */}
                            {!sub.lifetime_access && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedUserForExpiry(sub)}
                                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                                title="Edit expiry date"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Quick Add Section - Set expiry for approved users without one */}
                <div className="bg-slate-900/50 rounded-lg p-4 border border-white/10">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-green-400" />
                    Quick Set Expiry (for users without expiry date)
                  </h4>
                  <p className="text-gray-400 text-sm mb-3">
                    Go to &quot;Users &amp; Purchases&quot; panel to find approved users and click &quot;Set Expiry&quot; on their card.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Users & Purchases Panel */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-slate-800/50 border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => setShowUsersPurchasesPanel(!showUsersPurchasesPanel)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-400" />
                <CardTitle className="text-white">Users & Purchases</CardTitle>
                <Badge variant="outline" className="border-green-400 text-green-400">
                  {usersPurchasesSummary.paid_users} Paid
                </Badge>
                <Badge variant="outline" className="border-gray-400 text-gray-400">
                  {usersPurchasesSummary.free_users} Free
                </Badge>
              </div>
              {showUsersPurchasesPanel ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </CardHeader>
          
          {showUsersPurchasesPanel && (
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <Select value={usersPurchasesFilter} onValueChange={setUsersPurchasesFilter}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-white/10 text-white">
                    <SelectValue placeholder="User Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="paid">Paid Only</SelectItem>
                    <SelectItem value="free">Free Only</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={usersPurchasesCategory} onValueChange={setUsersPurchasesCategory}>
                  <SelectTrigger className="w-[180px] bg-slate-800 border-white/10 text-white">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="coaching">Coaching Only</SelectItem>
                    <SelectItem value="subscriptions">Subscriptions Only</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => fetchUsersPurchases()}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  disabled={loadingUsersPurchases}
                >
                  {loadingUsersPurchases ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </Button>
              </div>

              {/* Users List */}
              {loadingUsersPurchases ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {usersPurchases
                    .filter(user => {
                      if (usersPurchasesFilter === 'paid') return user.total_purchases > 0;
                      if (usersPurchasesFilter === 'free') return user.total_purchases === 0;
                      return true;
                    })
                    .filter(user => {
                      if (usersPurchasesCategory === 'coaching') return user.coaching_purchases?.length > 0;
                      if (usersPurchasesCategory === 'subscriptions') return user.subscription_purchases?.length > 0;
                      return true;
                    })
                    .map((user) => (
                      <div 
                        key={user.email} 
                        className="bg-slate-900/50 rounded-lg p-4 border border-white/10"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-white font-medium">{user.name || user.full_name || 'N/A'}</p>
                            <p className="text-sm text-gray-400">{user.email}</p>
                            <p className="text-xs text-gray-500 mt-1">Status: {user.status || 'active'}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              <Badge className={user.total_purchases > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                                {user.account_type || 'free'}
                              </Badge>
                              <Badge className={user.status === 'approved' ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}>
                                {user.status || 'pending'}
                              </Badge>
                            </div>
                            {/* Send Login Email Button - Always visible */}
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-500 text-white text-xs"
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API}/admin/send-login-email?token=${localStorage.getItem('adminToken')}`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ email: user.email })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    toast.success(`Login email sent to ${user.email}`);
                                  } else {
                                    toast.error(data.message || 'Failed to send email');
                                  }
                                } catch (err) {
                                  toast.error('Failed to send email');
                                }
                              }}
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              Send Login Email
                            </Button>
                          </div>
                        </div>

                        {/* TradingView Email */}
                        {user.tradingview_email && (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 mb-3">
                            <p className="text-xs text-amber-400 font-medium flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              TradingView: {user.tradingview_email}
                            </p>
                          </div>
                        )}

                        {/* Purchases */}
                        {user.total_purchases > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Coaching */}
                            {(usersPurchasesCategory === 'all' || usersPurchasesCategory === 'coaching') && user.coaching_purchases?.length > 0 && (
                              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                                <p className="text-purple-400 font-medium text-sm mb-2 flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  Coaching ({user.coaching_purchases.length})
                                </p>
                                <div className="space-y-1">
                                  {user.coaching_purchases.map((p, i) => (
                                    <div key={i} className="text-xs text-gray-300 flex justify-between">
                                      <span>{p.name}</span>
                                      <span className="text-green-400">${p.price?.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Subscriptions */}
                            {(usersPurchasesCategory === 'all' || usersPurchasesCategory === 'subscriptions') && user.subscription_purchases?.length > 0 && (
                              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                                <p className="text-blue-400 font-medium text-sm mb-2 flex items-center gap-1">
                                  <Zap className="w-4 h-4" />
                                  Subscriptions ({user.subscription_purchases.length})
                                </p>
                                <div className="space-y-1">
                                  {user.subscription_purchases.map((p, i) => (
                                    <div key={i} className="text-xs text-gray-300 flex justify-between">
                                      <span>{p.name}</span>
                                      <span className="text-green-400">${p.price?.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {user.total_purchases === 0 && (
                          <p className="text-gray-500 text-sm">No purchases yet</p>
                        )}

                        {/* Set Subscription Expiry Button */}
                        {user.total_purchases > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                            <div className="text-xs text-gray-400">
                              {user.subscription_expires ? (
                                <span>
                                  Expires: <span className={
                                    new Date(user.subscription_expires) < new Date() ? 'text-red-400' :
                                    (new Date(user.subscription_expires) - new Date()) / (1000 * 60 * 60 * 24) <= 3 ? 'text-orange-400' :
                                    'text-green-400'
                                  }>{new Date(user.subscription_expires).toLocaleDateString()}</span>
                                </span>
                              ) : (
                                <span className="text-yellow-400">No expiry date set</span>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedUserForExpiry({ id: user.id, email: user.email, name: user.name });
                                setShowSubscriptionCalendar(true);
                              }}
                              className="bg-amber-600 hover:bg-amber-500 text-xs"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              {user.subscription_expires ? 'Edit Expiry' : 'Set Expiry'}
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}

                  {usersPurchases.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No users found</p>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      {/* Moderator Notice */}
      {adminRole === 'moderator' && (
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-400 font-medium">Moderator Access</p>
              <p className="text-sm text-gray-300">
                You can verify payments, answer support tickets, and pause users. 
                Other settings are view-only for your role.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-white/10 backdrop-blur-xl border-white/20">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-purple-400" />
              <p className="text-3xl font-bold text-white">{summary.total}</p>
              <p className="text-sm text-gray-400">Completed Users</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 backdrop-blur-xl border-yellow-500/20">
            <CardContent className="p-4 text-center">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
              <p className="text-3xl font-bold text-yellow-400">{summary.incomplete_signups || 0}</p>
              <p className="text-sm text-gray-400">Incomplete</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 backdrop-blur-xl border-green-500/20">
            <CardContent className="p-4 text-center">
              <Wifi className="w-8 h-8 mx-auto mb-2 text-green-400" />
              <p className="text-3xl font-bold text-green-400">{summary.online}</p>
              <p className="text-sm text-gray-400">Online Now</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 backdrop-blur-xl border-purple-500/20">
            <CardContent className="p-4 text-center">
              <Zap className="w-8 h-8 mx-auto mb-2 text-purple-400" />
              <p className="text-3xl font-bold text-purple-400">{summary.trial_users || 0}</p>
              <p className="text-sm text-gray-400">Trial Users</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 backdrop-blur-xl border-emerald-500/20">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-3xl font-bold text-emerald-400">{summary.paid_users || 0}</p>
              <p className="text-sm text-gray-400">Paid Subscribers</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/10 backdrop-blur-xl border-orange-500/20">
            <CardContent className="p-4 text-center">
              <Ban className="w-8 h-8 mx-auto mb-2 text-orange-400" />
              <p className="text-3xl font-bold text-orange-400">{summary.blocked + (summary.trial_expired || 0)}</p>
              <p className="text-sm text-gray-400">Paused/Blocked</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Market Manipulation Alert Section */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className={`backdrop-blur-xl border-2 transition-all ${
          marketAlertEnabled 
            ? 'bg-red-900/40 border-red-500/60 shadow-lg shadow-red-500/20' 
            : 'bg-white/10 border-white/20'
        }`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${marketAlertEnabled ? 'bg-red-500/30 animate-pulse' : 'bg-gray-500/20'}`}>
                  <AlertTriangle className={`w-6 h-6 ${marketAlertEnabled ? 'text-red-400' : 'text-gray-400'}`} />
                </div>
                <div>
                  <CardTitle className={`flex items-center gap-2 ${marketAlertEnabled ? 'text-red-400' : 'text-white'}`}>
                    🚨 Market Manipulation Alert
                  </CardTitle>
                  <CardDescription className={marketAlertEnabled ? 'text-red-300/80' : 'text-gray-400'}>
                    {marketAlertEnabled 
                      ? 'ALERT IS ACTIVE - Users will see a warning when accessing trading features'
                      : 'Enable this to warn users about dangerous market conditions'
                    }
                  </CardDescription>
                </div>
              </div>
              {/* Alert Toggle Button */}
              <Button
                onClick={toggleMarketAlert}
                disabled={togglingMarketAlert}
                className={`${
                  marketAlertEnabled 
                    ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                    : 'bg-gray-600 hover:bg-gray-700'
                } text-white transition-colors min-w-[140px]`}
                data-testid="market-alert-toggle"
              >
                {togglingMarketAlert ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : marketAlertEnabled ? (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Alert ON
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-4 h-4 mr-2" />
                    Alert OFF
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {marketAlertEnabled && (
            <CardContent>
              <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4">
                <p className="text-red-200 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Active Alert Message:</strong> "⚠️ Market Manipulation Alert: Trading is not recommended at this time due to unusual market conditions."
                    <br />
                    <span className="text-red-300/70 text-xs mt-1 block">
                      Users will see this warning when they access the Trading Session Manager or try to start a trading session.
                    </span>
                  </span>
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Promotion Management Section */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Promotion / Discount Banner
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Display a promotional banner with discount code on the home page for visitors
                </CardDescription>
              </div>
              {/* Quick Toggle Button */}
              <Button
                onClick={() => quickTogglePromotion(!promoEnabled)}
                className={`${
                  promoEnabled 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-gray-600 hover:bg-gray-700'
                } text-white transition-colors`}
              >
                {promoEnabled ? (
                  <>
                    <ToggleRight className="w-4 h-4 mr-2" />
                    Banner ON
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-4 h-4 mr-2" />
                    Banner OFF
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Tag className={`w-5 h-5 ${promoEnabled ? 'text-green-400' : 'text-gray-400'}`} />
                <div>
                  <p className="text-white font-medium">Enable Promotion Banner</p>
                  <p className="text-xs text-gray-400">Show discount banner on the home page</p>
                </div>
              </div>
              <Switch
                checked={promoEnabled}
                onCheckedChange={setPromoEnabled}
              />
            </div>
            
            {/* Promotion Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">Discount Code</label>
                <Input
                  placeholder="e.g., SAVE20"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">Discount Amount</label>
                <Select value={String(promoDiscount)} onValueChange={(v) => setPromoDiscount(Number(v))}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Select %" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="5" className="text-white">5% off</SelectItem>
                    <SelectItem value="10" className="text-white">10% off</SelectItem>
                    <SelectItem value="15" className="text-white">15% off</SelectItem>
                    <SelectItem value="20" className="text-white">20% off</SelectItem>
                    <SelectItem value="25" className="text-white">25% off</SelectItem>
                    <SelectItem value="30" className="text-white">30% off</SelectItem>
                    <SelectItem value="40" className="text-white">40% off</SelectItem>
                    <SelectItem value="50" className="text-white">50% off</SelectItem>
                    <SelectItem value="60" className="text-white">60% off</SelectItem>
                    <SelectItem value="70" className="text-white">70% off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">Banner Color</label>
                <Select value={promoColor} onValueChange={setPromoColor}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="yellow" className="text-yellow-400">🟡 Yellow</SelectItem>
                    <SelectItem value="green" className="text-green-400">🟢 Green</SelectItem>
                    <SelectItem value="blue" className="text-blue-400">🔵 Blue</SelectItem>
                    <SelectItem value="red" className="text-red-400">🔴 Red</SelectItem>
                    <SelectItem value="purple" className="text-purple-400">🟣 Purple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">Promotion Text</label>
              <Input
                placeholder="e.g., 🎉 New Year Special! Get 20% off your subscription!"
                value={promoText}
                onChange={(e) => setPromoText(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
            </div>
            
            {/* Flash Sale Countdown Timer */}
            <div className="space-y-2 p-4 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg">
              <label className="text-sm font-medium text-red-300 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Flash Sale Countdown Timer (Optional)
              </label>
              <Input
                type="datetime-local"
                value={promoEndTime}
                onChange={(e) => setPromoEndTime(e.target.value)}
                className="bg-white/10 border-red-500/30 text-white"
              />
              <p className="text-xs text-gray-400">
                Set an end date/time to show a countdown timer on the banner. Leave empty for no countdown.
              </p>
              {promoEndTime && (
                <div className="mt-2 p-2 bg-black/30 rounded-lg">
                  <p className="text-xs text-red-300">
                    ⏰ Sale ends: <strong>{new Date(promoEndTime).toLocaleString()}</strong>
                  </p>
                  {new Date(promoEndTime) < new Date() && (
                    <p className="text-xs text-amber-400 mt-1">
                      ⚠️ This date is in the past. The countdown will show as expired.
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Preview */}
            {(promoCode || promoText) && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">Preview</label>
                <div className={`p-3 rounded-lg border ${
                  promoColor === 'green' ? 'bg-green-500/20 border-green-500/30' :
                  promoColor === 'blue' ? 'bg-blue-500/20 border-blue-500/30' :
                  promoColor === 'red' ? 'bg-red-500/20 border-red-500/30' :
                  promoColor === 'purple' ? 'bg-purple-500/20 border-purple-500/30' :
                  'bg-yellow-500/20 border-yellow-500/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      promoColor === 'green' ? 'text-green-300' :
                      promoColor === 'blue' ? 'text-blue-300' :
                      promoColor === 'red' ? 'text-red-300' :
                      promoColor === 'purple' ? 'text-purple-300' :
                      'text-yellow-300'
                    }`}>
                      {promoText || 'Your promotion text here'}
                    </span>
                    {promoCode && (
                      <span className={`font-mono font-bold ${
                        promoColor === 'green' ? 'text-green-300' :
                        promoColor === 'blue' ? 'text-blue-300' :
                        promoColor === 'red' ? 'text-red-300' :
                        promoColor === 'purple' ? 'text-purple-300' :
                        'text-yellow-300'
                      }`}>
                        Code: {promoCode}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <Button
              onClick={savePromotion}
              disabled={savingPromo}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              {savingPromo ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Promotion Settings
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* PWA Update Push Card */}
        <Card className="bg-white/10 backdrop-blur-xl border-white/20 mt-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Push App Update (PWA)
            </CardTitle>
            <CardDescription className="text-gray-400">
              Force update the app for all users. Their browsers will prompt them to refresh and get the new version.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Version Info */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Upload className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-medium">
                    Current Version: <span className="text-blue-400 font-mono">v{appVersion}</span>
                  </p>
                  <p className="text-sm text-gray-400">
                    {lastUpdatePushed 
                      ? `Last pushed: ${new Date(lastUpdatePushed).toLocaleString()}`
                      : 'No updates pushed yet'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Update Message Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">Update Message (Optional)</label>
              <Input
                placeholder="e.g., New features added! Bug fixes and performance improvements."
                value={updateMessage}
                onChange={(e) => setUpdateMessage(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
              <p className="text-xs text-gray-500">
                This message will be shown to users when they receive the update notification.
              </p>
            </div>

            {/* Push Update Button */}
            <Button
              onClick={pushAppUpdate}
              disabled={pushingUpdate}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-6"
            >
              {pushingUpdate ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Pushing Update...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Push Update to All Users
                </>
              )}
            </Button>

            <div className="text-xs text-gray-500 p-2 bg-white/5 rounded">
              📱 <strong>How it works:</strong> When you push an update, all users with the PWA installed will see a 
              notification prompting them to refresh. The app will automatically clear old caches and load the new version.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Email Codes Section - SendGrid failsafe */}
      <div className="max-w-6xl mx-auto mb-8" data-testid="pending-codes-section">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
            onClick={() => {
              const next = !showPendingCodes;
              setShowPendingCodes(next);
              if (next) fetchPendingCodes();
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-yellow-400" />
                  Pending Email Codes
                  <Badge className="bg-amber-500/80 text-white ml-2">
                    {pendingCodes.counts.verification_active + pendingCodes.counts.reset_active} active
                  </Badge>
                </CardTitle>
                <CardDescription className="text-gray-400 hidden md:block">
                  Hand verification &amp; reset codes to users when email delivery fails
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); fetchPendingCodes(); }}
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="pending-codes-refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingPendingCodes ? 'animate-spin' : ''}`} />
                </Button>
                {showPendingCodes ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
              </div>
            </div>
          </CardHeader>
          {showPendingCodes && (
            <CardContent className="pt-0">
              <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-xs">
                <strong>How to use:</strong> If a user says they didn't receive their signup verification or password reset email, find their email below and share the <strong>code</strong> directly with them (via chat, phone, etc.). Codes expire 15 minutes after creation.
              </div>
              <div className="flex flex-col md:flex-row gap-3 mb-4">
                <input
                  type="text"
                  placeholder="Filter by email…"
                  value={codeFilter}
                  onChange={(e) => setCodeFilter(e.target.value)}
                  className="flex-1 bg-slate-900/60 border border-white/20 text-white placeholder-gray-500 rounded-lg px-3 py-2 text-sm"
                  data-testid="pending-codes-filter"
                />
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showExpiredCodes}
                    onChange={(e) => setShowExpiredCodes(e.target.checked)}
                    className="rounded border-white/30"
                  />
                  Show expired
                </label>
              </div>

              {/* Password Reset Codes */}
              <div className="mb-5">
                <h3 className="text-sm font-bold text-emerald-300 mb-2 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> Password Reset Codes
                  <span className="text-xs text-gray-400 font-normal">({pendingCodes.counts.reset_active} active)</span>
                </h3>
                {(() => {
                  const filtered = pendingCodes.reset_codes.filter(c =>
                    (showExpiredCodes || !c.expired) &&
                    (!codeFilter || (c.email || '').toLowerCase().includes(codeFilter.toLowerCase()))
                  );
                  if (filtered.length === 0) {
                    return <p className="text-xs text-gray-500 italic">No {showExpiredCodes ? '' : 'active '}reset codes.</p>;
                  }
                  return (
                    <div className="space-y-1.5">
                      {filtered.map((c, idx) => (
                        <div
                          key={`r-${idx}`}
                          className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border ${c.expired ? 'bg-slate-800/40 border-white/5 opacity-60' : 'bg-emerald-500/5 border-emerald-500/20'}`}
                          data-testid={`reset-code-row-${idx}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white truncate" data-testid={`reset-code-email-${idx}`}>{c.email}</p>
                            <p className="text-[10px] text-gray-500">
                              {c.expired ? 'Expired ' : 'Expires '} {c.expires_at ? new Date(c.expires_at).toLocaleTimeString() : '—'}
                            </p>
                          </div>
                          <code className="font-mono text-lg font-bold text-emerald-300 tracking-wider px-2" data-testid={`reset-code-value-${idx}`}>
                            {c.code}
                          </code>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyCodeToClipboard(c.code, c.email)}
                            className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                            data-testid={`reset-code-copy-${idx}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Verification Codes */}
              <div>
                <h3 className="text-sm font-bold text-blue-300 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Signup Verification Codes
                  <span className="text-xs text-gray-400 font-normal">({pendingCodes.counts.verification_active} active)</span>
                </h3>
                {(() => {
                  const filtered = pendingCodes.verification_codes.filter(c =>
                    (showExpiredCodes || !c.expired) &&
                    (!codeFilter || (c.email || '').toLowerCase().includes(codeFilter.toLowerCase()))
                  );
                  if (filtered.length === 0) {
                    return <p className="text-xs text-gray-500 italic">No {showExpiredCodes ? '' : 'active '}verification codes.</p>;
                  }
                  return (
                    <div className="space-y-1.5">
                      {filtered.map((c, idx) => (
                        <div
                          key={`v-${idx}`}
                          className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border ${c.expired ? 'bg-slate-800/40 border-white/5 opacity-60' : 'bg-blue-500/5 border-blue-500/20'}`}
                          data-testid={`verification-code-row-${idx}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white truncate" data-testid={`verification-code-email-${idx}`}>{c.email}</p>
                            <p className="text-[10px] text-gray-500">
                              {c.expired ? 'Expired ' : 'Expires '} {c.expires_at ? new Date(c.expires_at).toLocaleTimeString() : '—'}
                            </p>
                          </div>
                          <code className="font-mono text-lg font-bold text-blue-300 tracking-wider px-2" data-testid={`verification-code-value-${idx}`}>
                            {c.code}
                          </code>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyCodeToClipboard(c.code, c.email)}
                            className="border-blue-500/40 text-blue-200 hover:bg-blue-500/10"
                            data-testid={`verification-code-copy-${idx}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Payment Proofs Section - Collapsible */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
            onClick={() => setShowPaymentProofs(!showPaymentProofs)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Payment Proofs
                  {unreadProofs > 0 && (
                    <Badge className="bg-red-500 text-white ml-2">{unreadProofs} new</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-gray-400 hidden md:block">
                  Review payment screenshots submitted by users
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchPaymentProofs();
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
                {showPaymentProofs ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
          {showPaymentProofs && (
            <CardContent className="space-y-4">
              {paymentProofs.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No payment proofs pending</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {paymentProofs.map((proof) => (
                    <div 
                      key={proof.id} 
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        proof.status === 'verified' 
                          ? 'bg-green-500/10 border-green-500/30' 
                          : proof.status === 'rejected'
                          ? 'bg-red-500/10 border-red-500/30'
                          : !proof.viewed 
                          ? 'bg-yellow-500/10 border-yellow-500/30'
                          : 'bg-white/5 border-white/20'
                      }`}
                      onClick={() => {
                        setSelectedProof(proof);
                        if (!proof.viewed) markProofViewed(proof.id);
                      }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">{proof.payer_name}</p>
                          <p className="text-sm text-gray-400">{proof.email}</p>
                          {proof.plan && (
                            <Badge className={`mt-1 ${proof.plan === 'premium' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                              {proof.plan === 'premium' ? 'Premium $59.99' : 'Essential $20'}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!proof.viewed && (
                            <Badge className="bg-yellow-500/20 text-yellow-300 text-xs">New</Badge>
                          )}
                          {proof.status === 'verified' && (
                            <Badge className="bg-green-500/20 text-green-300">Verified</Badge>
                          )}
                          {proof.status === 'rejected' && (
                            <Badge className="bg-red-500/20 text-red-300">Rejected</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{new Date(proof.created_at).toLocaleString()}</span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          Click to view
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      {/* Payment Proof Modal */}
      {selectedProof && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10 max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setSelectedProof(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            <div className="bg-gradient-to-r from-blue-500 to-purple-500 px-6 py-4">
              <h2 className="text-xl font-bold text-white">Payment Proof</h2>
              <p className="text-white/80">{selectedProof.email}</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Payer Name</p>
                  <p className="text-white font-medium">{selectedProof.payer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Submitted</p>
                  <p className="text-white">{new Date(selectedProof.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Expires</p>
                  <p className="text-yellow-400">{new Date(selectedProof.expires_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Status</p>
                  <Badge className={`${
                    selectedProof.status === 'verified' 
                      ? 'bg-green-500/20 text-green-300' 
                      : selectedProof.status === 'rejected'
                      ? 'bg-red-500/20 text-red-300'
                      : 'bg-yellow-500/20 text-yellow-300'
                  }`}>
                    {selectedProof.status.charAt(0).toUpperCase() + selectedProof.status.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* Screenshot */}
              <div className="border border-white/20 rounded-lg overflow-hidden">
                <img 
                  src={selectedProof.screenshot} 
                  alt="Payment proof" 
                  className="w-full object-contain max-h-96"
                />
              </div>

              {/* Actions */}
              {selectedProof.status === 'pending' && (
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => verifyPaymentProof(selectedProof.id, 'verify')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Verify & Activate User
                  </Button>
                  <Button
                    onClick={() => verifyPaymentProof(selectedProof.id, 'reject')}
                    variant="outline"
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}

              <Button
                variant="ghost"
                onClick={() => deletePaymentProof(selectedProof.id)}
                className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Proof
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Management Section - Collapsible */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
            onClick={() => setShowReviewsPanel(!showReviewsPanel)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  User Reviews
                  {pendingReviewsCount > 0 && (
                    <Badge className="bg-yellow-500 text-black ml-2">{pendingReviewsCount} pending</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-gray-400 hidden md:block">
                  Manage user reviews for homepage display
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchReviews();
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
                {showReviewsPanel ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
          {showReviewsPanel && (
            <CardContent className="space-y-4">
            {reviews.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Star className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No reviews yet</p>
                <p className="text-sm">Request reviews from users using the "Review" button on user cards</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div 
                    key={review.id} 
                    className={`p-4 rounded-lg border ${
                      review.status === 'approved' 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : review.status === 'rejected'
                        ? 'bg-red-500/10 border-red-500/30'
                        : 'bg-yellow-500/10 border-yellow-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                          {review.user_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{review.user_name}</p>
                          <p className="text-sm text-gray-400">{review.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= review.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                        <Badge className={`ml-2 ${
                          review.status === 'approved' 
                            ? 'bg-green-500/20 text-green-300' 
                            : review.status === 'rejected'
                            ? 'bg-red-500/20 text-red-300'
                            : 'bg-yellow-500/20 text-yellow-300'
                        }`}>
                          {review.status === 'pending_approval' ? 'Pending' : review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="text-gray-300 mb-3">"{review.review_text}"</p>
                    
                    {/* Trades reviewed */}
                    {review.trades_reviewed && review.trades_reviewed.length > 0 && (
                      <div className="flex gap-2 mb-3 flex-wrap">
                        {review.trades_reviewed.map((trade, idx) => (
                          <Badge 
                            key={idx}
                            variant="outline"
                            className={`${
                              trade.result === 'win' ? 'bg-green-500/10 text-green-400 border-green-500/30' :
                              trade.result === 'loss' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                              'bg-gray-500/10 text-gray-400 border-gray-500/30'
                            }`}
                          >
                            {trade.asset}: {trade.result}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Submitted: {new Date(review.submitted_at).toLocaleString()}
                      </span>
                      
                      {review.status === 'pending_approval' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveReview(review.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectReview(review.id)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          )}
        </Card>
      </div>

      {/* Support Tickets Section - Collapsible */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
            onClick={() => setShowSupportTickets(!showSupportTickets)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-blue-400" />
                  Support Tickets
                  {openTicketsCount > 0 && (
                    <Badge className="bg-blue-500 text-white ml-2">{openTicketsCount} open</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-gray-400 hidden md:block">
                  View and manage visitor support requests (auto-close after 72hrs)
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchSupportTickets();
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
                {showSupportTickets ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
          {showSupportTickets && (
            <CardContent className="space-y-4">
              {supportTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No support tickets yet</p>
                  <p className="text-sm">Visitors can submit tickets from the homepage</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {supportTickets.map((ticket) => (
                    <div 
                      key={ticket.id} 
                      className={`p-4 rounded-xl border ${
                        ticket.status === 'open' 
                          ? 'bg-blue-500/10 border-blue-500/30' 
                          : ticket.status === 'auto_closed'
                            ? 'bg-yellow-500/10 border-yellow-500/30'
                            : 'bg-gray-500/10 border-gray-500/30'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-white">{ticket.subject}</h4>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                ticket.status === 'open' 
                                  ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                  : ticket.status === 'auto_closed'
                                    ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                                    : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              }`}
                            >
                              {ticket.status === 'auto_closed' ? 'Auto-closed (72h)' : ticket.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-400">{ticket.name} • {ticket.email}</p>
                        </div>
                        <span className="text-xs text-gray-500">
                          {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                      
                      <div className="bg-black/20 rounded-lg p-3 mb-3">
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">{ticket.message}</p>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <a 
                          href={`mailto:${ticket.email}?subject=Re: ${ticket.subject}`}
                          className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                        >
                          <Mail className="w-4 h-4" />
                          Reply via Email
                        </a>
                        
                        {ticket.status === 'open' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => closeSupportTicket(ticket.id)}
                            className="border-gray-500/30 text-gray-300 hover:bg-gray-500/10"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Close Ticket
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      {/* Coaching Management Section - Collapsible */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
            onClick={() => setShowCoachingPanel(!showCoachingPanel)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-400" />
                  Coaching Management
                  {pendingCoachingCount > 0 && (
                    <Badge className="bg-purple-500 text-white ml-2">{pendingCoachingCount} pending</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-gray-400 hidden md:block">
                  Manage availability, bookings, and coaching tickets
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchCoachingData();
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
                {showCoachingPanel ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
          {showCoachingPanel && (
            <CardContent className="space-y-6">
              {/* Add Availability Section */}
              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-4">
                <h4 className="font-medium text-purple-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Add Availability (EST Timezone)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Date</label>
                    <Input
                      type="date"
                      value={newAvailDate}
                      onChange={(e) => setNewAvailDate(e.target.value)}
                      className="bg-white/5 border-white/20 text-white"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Time Slots (comma separated)</label>
                    <Input
                      value={newAvailTimes}
                      onChange={(e) => setNewAvailTimes(e.target.value)}
                      placeholder="10:00, 14:00, 16:30"
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={addCoachingAvailability}
                      disabled={savingAvailability}
                      className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                    >
                      {savingAvailability ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                      Add Slots
                    </Button>
                  </div>
                </div>
              </div>

              {/* Current Availability */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    Available Slots
                  </h4>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const nm = coachingViewMonth === 0 ? 11 : coachingViewMonth - 1;
                        const ny = coachingViewMonth === 0 ? coachingViewYear - 1 : coachingViewYear;
                        setCoachingViewMonth(nm);
                        setCoachingViewYear(ny);
                        fetchCoachingData();
                      }}
                      className="text-gray-400"
                    >
                      ←
                    </Button>
                    <span className="text-sm text-gray-300">
                      {new Date(coachingViewYear, coachingViewMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const nm = coachingViewMonth === 11 ? 0 : coachingViewMonth + 1;
                        const ny = coachingViewMonth === 11 ? coachingViewYear + 1 : coachingViewYear;
                        setCoachingViewMonth(nm);
                        setCoachingViewYear(ny);
                        fetchCoachingData();
                      }}
                      className="text-gray-400"
                    >
                      →
                    </Button>
                  </div>
                </div>
                {coachingAvailability.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No availability set for this month
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {coachingAvailability.map(slot => (
                      <div 
                        key={slot.slot_id}
                        className={`p-3 rounded-lg border ${
                          slot.is_booked 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : 'bg-green-500/10 border-green-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-400">{slot.date}</p>
                            <p className={`text-sm font-bold ${slot.is_booked ? 'text-red-400' : 'text-green-400'}`}>
                              {slot.time_slot} EST
                            </p>
                            {slot.is_booked && (
                              <p className="text-xs text-red-300">Booked</p>
                            )}
                          </div>
                          {!slot.is_booked && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteCoachingSlot(slot.slot_id)}
                              className="text-red-400 hover:bg-red-500/10 p-1"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bookings */}
              <div className="space-y-3">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  Coaching Bookings
                </h4>
                {coachingBookings.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No bookings yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {coachingBookings.map(booking => (
                      <div 
                        key={booking.booking_id}
                        className={`p-4 rounded-lg border ${
                          booking.status === 'payment_submitted' 
                            ? 'bg-yellow-500/10 border-yellow-500/30' 
                            : booking.status === 'confirmed'
                            ? 'bg-green-500/10 border-green-500/30'
                            : booking.status === 'cancelled'
                            ? 'bg-red-500/10 border-red-500/30'
                            : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-bold text-white">{booking.user_name}</p>
                              <Badge className={`text-xs ${
                                booking.status === 'payment_submitted' ? 'bg-yellow-500' :
                                booking.status === 'confirmed' ? 'bg-green-500' :
                                booking.status === 'cancelled' ? 'bg-red-500' :
                                'bg-gray-500'
                              }`}>
                                {booking.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-400">{booking.user_email}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span className="text-purple-300">{booking.package_name}</span>
                              <span className="text-green-400 font-bold">${booking.price}</span>
                              <span className="text-gray-400">{booking.date} at {booking.time_slot} EST</span>
                            </div>
                            {booking.payment_proof && (
                              <div className="mt-2">
                                <a 
                                  href={booking.payment_proof.screenshot} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-400 hover:underline"
                                >
                                  View Payment Proof →
                                </a>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            {booking.status === 'payment_submitted' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => confirmCoachingBooking(booking.booking_id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => cancelCoachingBooking(booking.booking_id, 'Payment not verified')}
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {booking.status === 'confirmed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelCoachingBooking(booking.booking_id, 'Cancelled by admin')}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                Cancel Session
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Coaching Tickets */}
              <div className="space-y-3">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-blue-400" />
                  Coaching Tickets
                </h4>
                {coachingTickets.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No coaching tickets
                  </div>
                ) : (
                  <div className="space-y-2">
                    {coachingTickets.map(ticket => (
                      <div 
                        key={ticket.id}
                        className={`p-3 rounded-lg border ${
                          ticket.status === 'open' || ticket.status === 'payment_submitted'
                            ? 'bg-yellow-500/10 border-yellow-500/30' 
                            : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-white text-sm">{ticket.subject}</p>
                            <p className="text-xs text-gray-400">{ticket.user_email}</p>
                            <p className="text-xs text-gray-500 mt-1">{ticket.message}</p>
                          </div>
                          {(ticket.status === 'open' || ticket.status === 'payment_submitted' || ticket.status === 'confirmed') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => closeCoachingTicket(ticket.id)}
                              className="border-gray-500/30 text-gray-300 hover:bg-gray-500/10"
                            >
                              Close
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Products Management Section - Collapsible */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
            onClick={() => setShowProductsPanel(!showProductsPanel)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <Tag className="w-5 h-5 text-green-400" />
                  Products Management
                  <Badge className="bg-green-500/20 text-green-400 ml-2">{products.length} products</Badge>
                </CardTitle>
                <CardDescription className="text-gray-400 hidden md:block">
                  Manage store products, descriptions, and pricing
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchProducts();
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
                {showProductsPanel ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>

          {showProductsPanel && (
            <CardContent className="space-y-6">
              {/* Add/Edit Product Form */}
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl space-y-4">
                <h4 className="font-medium text-green-300 flex items-center gap-2">
                  {editingProduct ? (
                    <>
                      <Edit className="w-4 h-4" />
                      Edit Product
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add New Product
                    </>
                  )}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Product Name *</label>
                    <Input
                      placeholder="e.g., Bonbot Signal Generator"
                      value={productForm.name}
                      onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Category *</label>
                    <Select
                      value={productForm.category}
                      onValueChange={(value) => setProductForm(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="signal_generator">Signal Generator</SelectItem>
                        <SelectItem value="coaching">Coaching</SelectItem>
                        <SelectItem value="mentorship">Mentorship</SelectItem>
                        <SelectItem value="indicator">Indicator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Short Description</label>
                  <Input
                    placeholder="Brief description for product cards"
                    value={productForm.short_description}
                    onChange={(e) => setProductForm(prev => ({ ...prev, short_description: e.target.value }))}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Full Description</label>
                  <textarea
                    placeholder="Detailed description for product detail page"
                    value={productForm.full_description}
                    onChange={(e) => setProductForm(prev => ({ ...prev, full_description: e.target.value }))}
                    className="w-full h-24 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-gray-500 p-3 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Image URL</label>
                  <Input
                    placeholder="https://example.com/image.jpg"
                    value={productForm.image_url}
                    onChange={(e) => setProductForm(prev => ({ ...prev, image_url: e.target.value }))}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                  {productForm.image_url && (
                    <img 
                      src={productForm.image_url} 
                      alt="Preview" 
                      className="w-24 h-24 object-cover rounded-lg mt-2"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={productForm.price}
                      onChange={(e) => setProductForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Price Type</label>
                    <Select
                      value={productForm.price_type}
                      onValueChange={(value) => setProductForm(prev => ({ ...prev, price_type: value }))}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="one_time">One-time</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Alt. Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={productForm.secondary_price}
                      onChange={(e) => setProductForm(prev => ({ ...prev, secondary_price: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Alt. Type</label>
                    <Select
                      value={productForm.secondary_price_type || ''}
                      onValueChange={(value) => setProductForm(prev => ({ ...prev, secondary_price_type: value }))}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="one_time">One-time</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Features</label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a feature..."
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                    <Button onClick={addFeature} size="sm" className="bg-green-600 hover:bg-green-700">
                      Add
                    </Button>
                  </div>
                  {productForm.features.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {productForm.features.map((feature, idx) => (
                        <Badge 
                          key={idx} 
                          className="bg-green-500/20 text-green-300 cursor-pointer hover:bg-red-500/20 hover:text-red-300"
                          onClick={() => removeFeature(idx)}
                        >
                          {feature} ×
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Display Order</label>
                    <Input
                      type="number"
                      value={productForm.display_order}
                      onChange={(e) => setProductForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div className="space-y-2 flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={productForm.is_active}
                        onChange={(e) => setProductForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="rounded"
                      />
                      Active (visible in store)
                    </label>
                  </div>
                </div>

                {/* Payment Link Field */}
                <div className="space-y-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <label className="text-sm text-green-400 font-medium flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Default Payment Link (Square)
                  </label>
                  <Input
                    type="url"
                    placeholder="https://square.link/u/..."
                    value={productForm.payment_link}
                    onChange={(e) => setProductForm(prev => ({ ...prev, payment_link: e.target.value }))}
                    className="bg-white/10 border-green-500/30 text-white placeholder:text-gray-500"
                  />
                  <p className="text-xs text-gray-400">
                    Default payment link. If you have sub-products (pricing tiers), configure individual links below.
                  </p>
                </div>

                {/* Pro Upgrade Payment Link - Only for Bonbot Signal Generator */}
                {productForm.product_id === 'bonbot-signal-generator' && (
                  <div className="space-y-2 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg">
                    <label className="text-sm text-purple-400 font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Pro Upgrade Payment Link (Square)
                    </label>
                    <Input
                      type="url"
                      placeholder="https://square.link/u/... for Pro upgrade"
                      value={productForm.pro_upgrade_payment_link || ''}
                      onChange={(e) => setProductForm(prev => ({ ...prev, pro_upgrade_payment_link: e.target.value }))}
                      className="bg-white/10 border-purple-500/30 text-white placeholder:text-gray-500"
                    />
                    <p className="text-xs text-gray-400">
                      This link will be used when users upgrade to the <strong className="text-purple-300">Pro Binary Scalper Bot</strong>.
                      Users will be redirected here when they click "Pay with Card" in the upgrade modal.
                    </p>
                    {productForm.pro_upgrade_payment_link ? (
                      <p className="text-xs text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Pro upgrade payment link configured
                      </p>
                    ) : (
                      <p className="text-xs text-amber-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Using default link - configure a specific link for Pro upgrades
                      </p>
                    )}
                  </div>
                )}

                {/* Sub-Products Payment Links */}
                {productForm.sub_products && productForm.sub_products.length > 0 && (
                  <div className="space-y-3 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <label className="text-sm text-purple-400 font-medium flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Pricing Tier Payment Links
                    </label>
                    <p className="text-xs text-gray-400 mb-3">
                      Configure individual Square payment links for each pricing option.
                    </p>
                    <div className="space-y-3">
                      {productForm.sub_products.map((subProduct, index) => (
                        <div key={subProduct.sub_id || index} className="bg-slate-800/50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium text-sm">{subProduct.name}</span>
                            <span className="text-green-400 text-sm font-bold">
                              ${subProduct.price?.toFixed(2) || '0.00'}
                              {subProduct.price_type && subProduct.price_type !== 'one_time' && `/${subProduct.price_type}`}
                            </span>
                          </div>
                          <Input
                            type="url"
                            placeholder={`Square link for ${subProduct.name}...`}
                            value={subProduct.payment_link || ''}
                            onChange={(e) => {
                              const updatedSubProducts = [...productForm.sub_products];
                              updatedSubProducts[index] = {
                                ...updatedSubProducts[index],
                                payment_link: e.target.value
                              };
                              setProductForm(prev => ({ ...prev, sub_products: updatedSubProducts }));
                            }}
                            className="bg-white/10 border-purple-500/30 text-white placeholder:text-gray-500 text-sm"
                          />
                          {subProduct.payment_link ? (
                            <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Payment link configured
                            </p>
                          ) : (
                            <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> No payment link set
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={saveProduct}
                    disabled={savingProduct}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {savingProduct ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                  {editingProduct && (
                    <Button 
                      onClick={resetProductForm}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </div>

              {/* Products List */}
              <div className="space-y-3">
                <h4 className="font-medium text-white">All Products ({products.length})</h4>
                {products.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No products yet. Add your first product above.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {products.map((product) => (
                      <div 
                        key={product.product_id}
                        className={`p-4 rounded-xl border ${
                          product.is_active 
                            ? 'bg-white/5 border-white/10' 
                            : 'bg-gray-800/50 border-gray-700/50 opacity-60'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {product.image_url && (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h5 className="font-bold text-white truncate">{product.name}</h5>
                                <Badge className="mt-1 text-xs" variant="outline">
                                  {product.category.replace('_', ' ')}
                                </Badge>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startEditProduct(product)}
                                  className="text-blue-400 hover:bg-blue-500/10"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleProductActive(product)}
                                  className={product.is_active ? "text-yellow-400 hover:bg-yellow-500/10" : "text-green-400 hover:bg-green-500/10"}
                                >
                                  {product.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteProduct(product.product_id)}
                                  className="text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{product.short_description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-green-400 font-bold">${product.price}</span>
                              <span className="text-gray-500 text-sm">/{product.price_type}</span>
                              {product.secondary_price && (
                                <span className="text-gray-400 text-sm">
                                  or ${product.secondary_price}/{product.secondary_price_type}
                                </span>
                              )}
                            </div>
                            {/* Payment Link Indicator */}
                            {product.payment_link ? (
                              <div className="flex items-center gap-1 mt-2 text-xs text-green-400">
                                <CreditCard className="w-3 h-3" />
                                <span className="truncate max-w-[180px]">{product.payment_link}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 mt-2 text-xs text-amber-400">
                                <AlertCircle className="w-3 h-3" />
                                <span>No payment link set</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Site Content Management Section - Collapsible */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
            onClick={() => setShowSiteContentPanel(!showSiteContentPanel)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Site Content Management
                </CardTitle>
                <CardDescription className="text-gray-400 hidden md:block">
                  Edit About Us, Contact Info, Terms & Refund Policy
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {showSiteContentPanel ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
          {showSiteContentPanel && (
            <CardContent className="space-y-6">
              {/* About Us Section */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl space-y-4">
                <h4 className="font-medium text-blue-300">About Us Section</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-400">Title</label>
                    <Input
                      value={siteContent.aboutUs?.title || ''}
                      onChange={(e) => setSiteContent({
                        ...siteContent,
                        aboutUs: { ...siteContent.aboutUs, title: e.target.value }
                      })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Content</label>
                    <Textarea
                      value={siteContent.aboutUs?.content || ''}
                      onChange={(e) => setSiteContent({
                        ...siteContent,
                        aboutUs: { ...siteContent.aboutUs, content: e.target.value }
                      })}
                      className="bg-white/10 border-white/20 text-white min-h-[100px]"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Us Section */}
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl space-y-4">
                <h4 className="font-medium text-green-300">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Email</label>
                    <Input
                      value={siteContent.contactUs?.email || ''}
                      onChange={(e) => setSiteContent({
                        ...siteContent,
                        contactUs: { ...siteContent.contactUs, email: e.target.value }
                      })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Phone</label>
                    <Input
                      value={siteContent.contactUs?.phone || ''}
                      onChange={(e) => setSiteContent({
                        ...siteContent,
                        contactUs: { ...siteContent.contactUs, phone: e.target.value }
                      })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-400">Address</label>
                    <Input
                      value={siteContent.contactUs?.address || ''}
                      onChange={(e) => setSiteContent({
                        ...siteContent,
                        contactUs: { ...siteContent.contactUs, address: e.target.value }
                      })}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Service Terms Section */}
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-4">
                <h4 className="font-medium text-amber-300">Service Terms</h4>
                <Textarea
                  value={siteContent.serviceTerms || ''}
                  onChange={(e) => setSiteContent({
                    ...siteContent,
                    serviceTerms: e.target.value
                  })}
                  className="bg-white/10 border-white/20 text-white min-h-[120px]"
                  placeholder="Enter your service terms..."
                />
              </div>

              {/* Refund Policy Section */}
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl space-y-4">
                <h4 className="font-medium text-red-300">Refund Policy</h4>
                <Textarea
                  value={siteContent.refundPolicy || ''}
                  onChange={(e) => setSiteContent({
                    ...siteContent,
                    refundPolicy: e.target.value
                  })}
                  className="bg-white/10 border-white/20 text-white min-h-[120px]"
                  placeholder="Enter your refund policy..."
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  onClick={saveSiteContent}
                  disabled={savingSiteContent}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white"
                >
                  {savingSiteContent ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save All Content
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Education TV Section */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-xl"
            onClick={() => setEducationTVCollapsed(!educationTVCollapsed)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${educationTVCollapsed ? '-rotate-90' : ''}`} />
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Tv className="w-5 h-5" />
                    Tool & Education TV
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Upload educational videos for users. Content auto-deletes after 24 hours.
                  </CardDescription>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); fetchEducationContent(); }}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          {!educationTVCollapsed && (
          <CardContent className="space-y-6">
            {/* Add New Content Form */}
            <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-4">
              <h4 className="font-medium text-purple-300 flex items-center gap-2">
                <Play className="w-4 h-4" />
                Add New Video
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Video Title *</label>
                  <Input
                    placeholder="e.g., How to Read MACD Signals"
                    value={newEduTitle}
                    onChange={(e) => setNewEduTitle(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">YouTube/Vimeo URL *</label>
                  <div className="relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="https://youtube.com/watch?v=..."
                      value={newEduUrl}
                      onChange={(e) => setNewEduUrl(e.target.value)}
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-gray-300">Description (optional)</label>
                <Textarea
                  placeholder="Brief description of the video content..."
                  value={newEduDescription}
                  onChange={(e) => setNewEduDescription(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 min-h-[80px]"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Target Audience</label>
                  <Select value={newEduTargetType} onValueChange={setNewEduTargetType}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all" className="text-white">
                        <span className="flex items-center gap-2">
                          <Users2 className="w-4 h-4" />
                          All Users (Broadcast)
                        </span>
                      </SelectItem>
                      <SelectItem value="specific" className="text-white">
                        <span className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4" />
                          Specific Users
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {newEduTargetType === 'specific' && (
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Target Emails (comma-separated)</label>
                    <Input
                      placeholder="user1@email.com, user2@email.com"
                      value={newEduTargetUsers}
                      onChange={(e) => setNewEduTargetUsers(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                )}
              </div>
              
              <Button
                onClick={createEducationContent}
                disabled={savingEdu || !newEduTitle.trim() || !newEduUrl.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {savingEdu ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Add Video
                  </>
                )}
              </Button>
            </div>
            
            {/* Content List */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-200">Current Videos ({educationContent.length})</p>
              
              {educationContent.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Tv className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No education content uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {educationContent.map((item) => (
                    <div 
                      key={item.id}
                      className={`p-4 border rounded-lg ${
                        item.is_archived 
                          ? 'bg-amber-500/10 border-amber-500/30' 
                          : item.extended_hours 
                            ? 'bg-blue-500/10 border-blue-500/30'
                            : 'bg-white/5 border-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            item.is_archived 
                              ? 'bg-amber-500/20' 
                              : 'bg-purple-500/20'
                          }`}>
                            {item.is_archived ? (
                              <Archive className="w-5 h-5 text-amber-400" />
                            ) : (
                              <Tv className="w-5 h-5 text-purple-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-white">{item.title}</h4>
                              {item.is_archived && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                  ARCHIVED
                                </Badge>
                              )}
                              {!item.is_archived && item.extended_hours && (
                                <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                                  EXTENDED
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                              <span className="flex items-center gap-1">
                                {item.target_type === 'all' ? (
                                  <><Users2 className="w-3 h-3" /> All Users</>
                                ) : (
                                  <><UserCheck className="w-3 h-3" /> {item.target_users?.length || 0} Users</>
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" /> {item.viewed_by?.length || 0} views
                              </span>
                              <span className={`flex items-center gap-1 ${item.is_archived ? 'text-amber-400' : ''}`}>
                                <Clock className="w-3 h-3" /> 
                                {item.is_archived ? '∞ Permanent' : `${getTimeRemaining(item.expires_at)} left`}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          {/* Extend Duration Button - only show if not archived */}
                          {!item.is_archived && (
                            <div className="relative group">
                              <button
                                onClick={() => extendEducationContent(item.id, 72)}
                                className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                title="Extend to 72 hours"
                              >
                                <Timer className="w-4 h-4" />
                              </button>
                              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                Extend to 72h
                              </div>
                            </div>
                          )}
                          
                          {/* Archive Toggle Button */}
                          <div className="relative group">
                            <button
                              onClick={() => toggleArchiveEducation(item.id)}
                              className={`p-2 rounded-lg transition-colors ${
                                item.is_archived 
                                  ? 'text-amber-400 hover:bg-amber-500/10' 
                                  : 'text-gray-400 hover:bg-gray-500/10'
                              }`}
                              title={item.is_archived ? 'Remove from archive' : 'Archive (permanent)'}
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {item.is_archived ? 'Unarchive' : 'Archive (permanent)'}
                            </div>
                          </div>
                          
                          {/* Play Button */}
                          <a
                            href={item.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                            title="Watch video"
                          >
                            <Play className="w-4 h-4" />
                          </a>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => deleteEducationContent(item.id)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete video"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
          )}
        </Card>
      </div>

      {/* Community Chat Management Section */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader
            className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-xl"
            onClick={() => setCommunityChatCollapsed(!communityChatCollapsed)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${communityChatCollapsed ? '-rotate-90' : ''}`} />
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Community Chat Management
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Monitor chat messages, delete inappropriate content, and manage suspended users
                  </CardDescription>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); fetchChatMessages(); fetchSuspendedUsers(); }}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          {!communityChatCollapsed && (
          <CardContent className="space-y-6">
            {/* Suspended Users List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-200 flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-400" />
                  Suspended Users ({suspendedUsers.length})
                </p>
              </div>
              
              {loadingSuspended ? (
                <div className="text-center py-4">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                </div>
              ) : suspendedUsers.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No suspended users
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {suspendedUsers.map((user, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
                    >
                      <div>
                        <p className="text-white font-medium text-sm">{user.first_name || 'User'}</p>
                        <p className="text-gray-400 text-xs">{user.email}</p>
                        <p className="text-red-400 text-xs mt-1">
                          Suspended: {new Date(user.chat_suspended_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnsuspendUser(user.email)}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                      >
                        Unsuspend
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Chat Messages */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-200">
                  Recent Messages ({chatMessages.length})
                </p>
              </div>
              
              {loadingChat ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No chat messages yet</p>
                  <p className="text-sm text-gray-500">Click Refresh to load messages</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {chatMessages.map((msg) => (
                    <div 
                      key={msg.id}
                      className="p-3 bg-white/5 border border-white/10 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-bold">
                              {msg.user_name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium text-sm">{msg.user_name}</span>
                              <span className="text-gray-500 text-xs">
                                {new Date(msg.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-gray-300 text-sm mt-1">{msg.text}</p>
                            {msg.image_url && (
                              <img 
                                src={msg.image_url} 
                                alt="Attachment" 
                                className="mt-2 max-w-[150px] rounded-lg"
                              />
                            )}
                            {msg.replies && msg.replies.length > 0 && (
                              <p className="text-purple-400 text-xs mt-1">
                                {msg.replies.length} replies
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleSuspendUser(msg.user_id, msg.user_name)}
                            className="p-1.5 rounded hover:bg-orange-500/20 text-gray-500 hover:text-orange-400 transition-colors"
                            title="Suspend user"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteChatMessage(msg.id)}
                            className="p-1.5 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors"
                            title="Delete message"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
          )}
        </Card>
      </div>

      {/* Trade History Section */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div 
                className="flex-1 cursor-pointer"
                onClick={() => setShowTradeHistory(!showTradeHistory)}
              >
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Trade History
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTradeHistory ? 'rotate-180' : ''}`} />
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {showTradeHistory ? 'Click to collapse' : 'Click to expand'} • View all user trade reports (WIN/LOSS)
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTradeHistory(!showTradeHistory)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {showTradeHistory ? (
                    <>
                      <EyeOff className="w-4 h-4 mr-1" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-1" />
                      Show
                    </>
                  )}
                </Button>
                {showTradeHistory && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearTradeFilters}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Clear Filters
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        fetchTradeHistory();
                        fetchTradeStats();
                      }}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          
          {/* Collapsible Content */}
          {showTradeHistory && (
          <CardContent className="space-y-6">
            {/* Trade Stats Summary */}
            {tradeStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-center">
                  <p className="text-3xl font-bold text-blue-400">{tradeStats.time_periods?.today?.trades || 0}</p>
                  <p className="text-sm text-gray-400">Today's Trades</p>
                  <p className="text-xs text-blue-300 mt-1">
                    Win Rate: {tradeStats.time_periods?.today?.win_rate || 0}%
                  </p>
                </div>
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
                  <p className="text-3xl font-bold text-green-400">{tradeStats.time_periods?.this_week?.wins || 0}</p>
                  <p className="text-sm text-gray-400">Wins This Week</p>
                  <p className="text-xs text-green-300 mt-1">
                    of {tradeStats.time_periods?.this_week?.trades || 0} trades
                  </p>
                </div>
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
                  <p className="text-3xl font-bold text-red-400">{tradeStats.time_periods?.this_week?.losses || 0}</p>
                  <p className="text-sm text-gray-400">Losses This Week</p>
                  <p className="text-xs text-red-300 mt-1">
                    of {tradeStats.time_periods?.this_week?.trades || 0} trades
                  </p>
                </div>
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl text-center">
                  <p className="text-3xl font-bold text-purple-400">{tradeStats.time_periods?.all_time?.win_rate || 0}%</p>
                  <p className="text-sm text-gray-400">All-Time Win Rate</p>
                  <p className="text-xs text-purple-300 mt-1">
                    {tradeStats.time_periods?.all_time?.trades || 0} total trades
                  </p>
                </div>
              </div>
            )}
            
            {/* PRO vs Standard Mode Stats */}
            {tradeStats?.by_mode && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* PRO Stats */}
                <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-yellow-300 flex items-center gap-2">
                      <span className="text-lg">⚡</span> PRO Mode Stats
                    </h4>
                    <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                      {tradeStats.by_mode.pro?.trades || 0} trades
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-green-400">{tradeStats.by_mode.pro?.wins || 0}</p>
                      <p className="text-xs text-gray-400">Wins</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-red-400">{tradeStats.by_mode.pro?.losses || 0}</p>
                      <p className="text-xs text-gray-400">Losses</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className={`text-2xl font-bold ${(tradeStats.by_mode.pro?.win_rate || 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                        {tradeStats.by_mode.pro?.win_rate || 0}%
                      </p>
                      <p className="text-xs text-gray-400">Win Rate</p>
                    </div>
                  </div>
                </div>
                
                {/* Standard Stats */}
                <div className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-blue-300 flex items-center gap-2">
                      <span className="text-lg">📊</span> Standard Mode Stats
                    </h4>
                    <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                      {tradeStats.by_mode.standard?.trades || 0} trades
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-green-400">{tradeStats.by_mode.standard?.wins || 0}</p>
                      <p className="text-xs text-gray-400">Wins</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-red-400">{tradeStats.by_mode.standard?.losses || 0}</p>
                      <p className="text-xs text-gray-400">Losses</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className={`text-2xl font-bold ${(tradeStats.by_mode.standard?.win_rate || 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                        {tradeStats.by_mode.standard?.win_rate || 0}%
                      </p>
                      <p className="text-xs text-gray-400">Win Rate</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Symbols & Users Stats */}
            {tradeStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Top Symbols */}
                <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Top Trading Pairs
                  </h4>
                  <div className="space-y-2">
                    {(tradeStats.by_symbol || []).slice(0, 5).map((s, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-white">{s.symbol}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-green-400">{s.wins}W</span>
                          <span className="text-red-400">{s.losses}L</span>
                          <Badge variant="outline" className={`text-xs ${s.win_rate >= 50 ? 'border-green-500/30 text-green-400' : 'border-red-500/30 text-red-400'}`}>
                            {s.win_rate}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {(!tradeStats.by_symbol || tradeStats.by_symbol.length === 0) && (
                      <p className="text-gray-500 text-sm">No trades yet</p>
                    )}
                  </div>
                </div>

                {/* Top Traders */}
                <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                    <Trophy className="w-4 h-4" />
                    Top Traders
                  </h4>
                  <div className="space-y-2">
                    {(tradeStats.by_user || []).slice(0, 5).map((u, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-white truncate max-w-[150px]">{u.name || u.email}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">{u.total}</span>
                          <Badge variant="outline" className={`text-xs ${u.win_rate >= 50 ? 'border-green-500/30 text-green-400' : 'border-red-500/30 text-red-400'}`}>
                            {u.win_rate}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {(!tradeStats.by_user || tradeStats.by_user.length === 0) && (
                      <p className="text-gray-500 text-sm">No traders yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 p-4 bg-white/5 rounded-lg">
              <Input
                placeholder="Filter by email..."
                value={tradeFilters.user_email}
                onChange={(e) => handleTradeFilterChange('user_email', e.target.value)}
                className="w-48 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
              <Input
                placeholder="Filter by symbol..."
                value={tradeFilters.symbol}
                onChange={(e) => handleTradeFilterChange('symbol', e.target.value)}
                className="w-40 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
              <Select value={tradeFilters.result} onValueChange={(v) => handleTradeFilterChange('result', v)}>
                <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Result" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-white">All Results</SelectItem>
                  <SelectItem value="win" className="text-white">Wins Only</SelectItem>
                  <SelectItem value="loss" className="text-white">Losses Only</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="border-white/20 text-gray-300 self-center">
                {tradePagination.total} trades
              </Badge>
            </div>

            {/* Trade History Table */}
            <div className="overflow-x-auto">
              {loadingTrades ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : tradeHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No trade history found</p>
                  <p className="text-sm">Trades will appear here after users report WIN/LOSS</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Pair</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Signal</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Result</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Accuracy</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradeHistory.map((trade) => (
                      <tr key={trade.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white text-sm">{trade.user_name || 'Unknown'}</p>
                            <p className="text-gray-500 text-xs">{trade.user_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-white font-medium">{trade.asset_name || trade.symbol || 'Unknown'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${
                            trade.signal_direction === 'BUY' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                              : trade.signal_direction === 'SELL'
                                ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                          }`}>
                            {trade.signal_direction === 'BUY' ? '🟢 BUY' : trade.signal_direction === 'SELL' ? '🔴 SELL' : '—'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${
                            trade.result === 'win' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }`}>
                            {trade.result === 'win' ? '✅ WIN' : '❌ LOSS'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-sm ${
                            trade.accuracy >= 80 ? 'text-green-400' : trade.accuracy >= 60 ? 'text-yellow-400' : 'text-gray-400'
                          }`}>
                            {trade.accuracy ? `${trade.accuracy}%` : '—'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-400 text-sm">
                            {trade.timestamp ? new Date(trade.timestamp).toLocaleString() : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {tradePagination.has_more && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={loadMoreTrades}
                  disabled={loadingTrades}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {loadingTrades ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-2" />
                  )}
                  Load More ({tradePagination.total - tradePagination.offset - tradePagination.limit} remaining)
                </Button>
              </div>
            )}
          </CardContent>
          )}
        </Card>
      </div>

      {/* Referral Code Management Section */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Referral Codes
            </CardTitle>
            <CardDescription className="text-gray-400">
              Create referral codes for valuable customers. They can share with others for signup discounts.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Create New Code */}
            <div className="p-4 bg-white/5 rounded-lg space-y-3">
              <p className="text-sm font-medium text-gray-200 mb-2">Create New Referral Code</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  placeholder="Code (e.g., JOHN20)"
                  value={newRefCode}
                  onChange={(e) => setNewRefCode(e.target.value.toUpperCase())}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                />
                <Input
                  placeholder="Referrer Name"
                  value={newRefName}
                  onChange={(e) => setNewRefName(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                />
                <Select value={String(newRefDiscount)} onValueChange={(v) => setNewRefDiscount(Number(v))}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Discount %" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="5" className="text-white">5% off</SelectItem>
                    <SelectItem value="10" className="text-white">10% off</SelectItem>
                    <SelectItem value="15" className="text-white">15% off</SelectItem>
                    <SelectItem value="20" className="text-white">20% off</SelectItem>
                    <SelectItem value="25" className="text-white">25% off</SelectItem>
                    <SelectItem value="30" className="text-white">30% off</SelectItem>
                    <SelectItem value="50" className="text-white">50% off</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={createReferralCode}
                  disabled={savingRef || !newRefCode || !newRefName}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {savingRef ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Create Code
                </Button>
              </div>
            </div>
            
            {/* Existing Codes */}
            {referralCodes.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-200">Active Referral Codes</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {referralCodes.map((ref) => (
                    <div key={ref.code} className={`flex items-center justify-between p-3 rounded-lg ${ref.is_active ? 'bg-green-500/10 border border-green-500/20' : 'bg-gray-500/10 border border-gray-500/20'}`}>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={`font-mono ${ref.is_active ? 'text-green-300 border-green-500/50' : 'text-gray-400 border-gray-500/50'}`}>
                          {ref.code}
                        </Badge>
                        <span className="text-white font-medium">{ref.referrer_name}</span>
                        <Badge className="bg-purple-500/20 text-purple-300">
                          {ref.discount_percent}% off
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {ref.users_referred || 0} users referred
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleReferralCode(ref.code, ref.is_active)}
                          className={ref.is_active ? 'text-green-400' : 'text-gray-400'}
                        >
                          {ref.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteReferralCode(ref.code)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400 py-4">
                No referral codes created yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-yellow-500/10 backdrop-blur-xl border-yellow-500/20">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-yellow-400">{summary.pending_verification}</p>
              <p className="text-xs text-gray-400">Pending Email</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 backdrop-blur-xl border-blue-500/20">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-400">{summary.pending_approval}</p>
              <p className="text-xs text-gray-400">Pending Payment</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 backdrop-blur-xl border-emerald-500/20">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">{summary.approved}</p>
              <p className="text-xs text-gray-400">Active Subscriptions</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 backdrop-blur-xl border-red-500/20">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{summary.rejected}</p>
              <p className="text-xs text-gray-400">Rejected</p>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Online Users Section */}
      {onlineUsers.length > 0 && (
        <div className="max-w-6xl mx-auto mb-6">
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Wifi className="w-5 h-5 text-green-400" />
              <span className="text-green-300 font-medium">Currently Online ({onlineUsers.length})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {onlineUsers.map(user => (
                <Badge key={user.id} variant="outline" className="bg-green-500/20 text-green-300 border-green-500/30">
                  {user.name} ({user.email})
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pending Approval Alert */}
      {summary.pending_approval > 0 && (
        <div className="max-w-6xl mx-auto mb-6">
          <div className="flex items-center gap-3 p-4 bg-blue-500/20 border border-blue-500/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-400" />
            <p className="text-blue-300">
              You have <strong>{summary.pending_approval}</strong> user(s) with pending payment - verify and approve after payment confirmation
            </p>
          </div>
        </div>
      )}

      {/* Send Login Email Section */}
      <div className="max-w-6xl mx-auto mb-6">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5 text-green-400" />
              Send Login Email
            </CardTitle>
            <CardDescription className="text-gray-400">
              Send a login link to any registered user
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                type="email"
                placeholder="Enter user's email address..."
                value={loginEmailAddress}
                onChange={(e) => setLoginEmailAddress(e.target.value)}
                className="flex-1 bg-slate-800 border-white/10 text-white placeholder:text-gray-500"
                onKeyDown={(e) => e.key === 'Enter' && sendLoginEmail()}
              />
              <Button
                onClick={sendLoginEmail}
                disabled={sendingLoginEmail || !loginEmailAddress.trim()}
                className="bg-green-600 hover:bg-green-500 text-white"
              >
                {sendingLoginEmail ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Login Email
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              The user will receive an email with a link to login to their signal generator dashboard.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Guest Lists */}
      <div className="max-w-6xl mx-auto">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Guest Users
            </CardTitle>
            <CardDescription className="text-gray-400">
              Manage guest registrations and approvals
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="pending_approval" className="w-full">
              <TabsList className="grid w-full grid-cols-5 bg-white/5">
                <TabsTrigger value="pending_approval" className="data-[state=active]:bg-blue-500/20">
                  Pending ({summary.pending_approval})
                </TabsTrigger>
                <TabsTrigger value="approved" className="data-[state=active]:bg-green-500/20">
                  Approved ({summary.approved})
                </TabsTrigger>
                <TabsTrigger value="blocked" className="data-[state=active]:bg-orange-500/20">
                  Blocked ({summary.blocked})
                </TabsTrigger>
                <TabsTrigger value="pending_verification" className="data-[state=active]:bg-yellow-500/20">
                  Unverified ({summary.pending_verification})
                </TabsTrigger>
                <TabsTrigger value="rejected" className="data-[state=active]:bg-red-500/20">
                  Rejected ({summary.rejected})
                </TabsTrigger>
              </TabsList>

              {['pending_approval', 'approved', 'blocked', 'pending_verification', 'rejected'].map((status) => (
                <TabsContent key={status} value={status} className="mt-4">
                  {(status === 'blocked' ? guests.filter(g => g.is_blocked) : filterGuestsByStatus(status)).length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No {status.replace('_', ' ')} guests</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(status === 'blocked' ? guests.filter(g => g.is_blocked) : filterGuestsByStatus(status)).map((guest) => (
                        <div
                          key={guest.id}
                          className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-white/5 rounded-lg gap-4"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="relative">
                                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                  {guest.name?.charAt(0).toUpperCase() || 'G'}
                                </div>
                                {guest.is_online && (
                                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900"></div>
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  {/* Country Flag */}
                                  {guest.country && (
                                    <span className="text-lg" title={guest.country}>
                                      {getCountryFlag(guest.country)}
                                    </span>
                                  )}
                                  <p className="font-medium text-white">{guest.name}</p>
                                  {guest.is_online && <span className="text-xs text-green-400">● Online</span>}
                                  {!guest.country && (
                                    <span className="text-xs text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded">
                                      No country
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-400">{guest.email}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                              <span>Registered: {formatDate(guest.created_at)}</span>
                              {guest.verified_at && <span>• Verified: {formatDate(guest.verified_at)}</span>}
                              {guest.signals_generated > 0 && <span>• Signals: {guest.signals_generated}</span>}
                              {guest.block_type && <span>• Block: {guest.block_type}</span>}
                            </div>
                            {/* Subscription Info Row */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {/* Clickable Subscription Toggle */}
                              <button
                                onClick={() => toggleSubscriptionType(guest.id, guest.subscription_type)}
                                disabled={processing === guest.id}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                                  guest.subscription_type === 'trial'
                                    ? 'bg-purple-500/20 text-purple-400 border-purple-500/30 hover:bg-purple-500/30'
                                    : guest.payment_status === 'paid'
                                      ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
                                      : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/30'
                                }`}
                                title={`Click to toggle: ${guest.subscription_type === 'trial' ? 'Trial → Paid' : 'Paid → Trial'}`}
                              >
                                {guest.subscription_type === 'trial' ? (
                                  <><Zap className="w-3 h-3 mr-1" />Trial</>
                                ) : (
                                  <><DollarSign className="w-3 h-3 mr-1" />Paid</>
                                )}
                                <ToggleRight className="w-3 h-3 ml-1" />
                              </button>
                              {guest.subscription_type === 'trial' && guest.trial_hours_left !== null && (
                                <Badge variant="outline" className="bg-purple-500/10 text-purple-300 border-purple-500/20">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {guest.trial_hours_left > 0 ? `${guest.trial_hours_left}h left` : 'Expired'}
                                </Badge>
                              )}
                              {/* Admin Referral Code Tag */}
                              {guest.referral_code && (
                                <Badge variant="outline" className="bg-pink-500/20 text-pink-300 border-pink-500/30">
                                  <Gift className="w-3 h-3 mr-1" />
                                  Ref: {guest.referral_code}
                                  {guest.referrer_name && ` (${guest.referrer_name})`}
                                  {guest.discount_applied > 0 && ` -${guest.discount_applied}%`}
                                </Badge>
                              )}
                              {/* User Referral Tag - WHO REFERRED THIS USER */}
                              {guest.referred_by_user_name && (
                                <Badge variant="outline" className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                                  <Users className="w-3 h-3 mr-1" />
                                  Referred by: {guest.referred_by_user_name}
                                </Badge>
                              )}
                              {/* User's Referral Stats - HOW MANY THEY REFERRED */}
                              {(guest.referral_count > 0 || guest.bonus_days > 0) && (
                                <Badge variant="outline" className="bg-green-500/20 text-green-300 border-green-500/30">
                                  <Trophy className="w-3 h-3 mr-1" />
                                  {guest.referral_count} referrals • {guest.bonus_days || 0} bonus days
                                </Badge>
                              )}
                              {/* Extension Days */}
                              {guest.manual_extension_days > 0 && (
                                <Badge variant="outline" className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  +{guest.manual_extension_days} days extended
                                </Badge>
                              )}
                              {/* Trade Stats Badge */}
                              {guest.trade_stats && (
                                <Badge 
                                  variant="outline" 
                                  className={`cursor-pointer ${
                                    guest.trade_stats.last_24h?.trades > 0 
                                      ? guest.trade_stats.last_24h.win_rate >= 60 
                                        ? 'bg-green-500/20 text-green-300 border-green-500/30' 
                                        : guest.trade_stats.last_24h.win_rate >= 40
                                          ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                                          : 'bg-red-500/20 text-red-300 border-red-500/30'
                                      : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                  }`}
                                  onClick={() => setExpandedUser(expandedUser === guest.id ? null : guest.id)}
                                >
                                  <TrendingUp className="w-3 h-3 mr-1" />
                                  24h: {guest.trade_stats.last_24h?.trades || 0} trades
                                  {guest.trade_stats.last_24h?.trades > 0 && ` (${guest.trade_stats.last_24h.win_rate}%)`}
                                  {expandedUser === guest.id ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                                </Badge>
                              )}
                              {/* Profile Details Badge */}
                              <Badge 
                                variant="outline" 
                                className="cursor-pointer bg-indigo-500/20 text-indigo-300 border-indigo-500/30 hover:bg-indigo-500/30"
                                onClick={() => setExpandedProfile(expandedProfile === guest.id ? null : guest.id)}
                              >
                                <User className="w-3 h-3 mr-1" />
                                Profile
                                {expandedProfile === guest.id ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                              </Badge>
                            </div>
                            
                            {/* Expanded Trade Stats */}
                            {expandedUser === guest.id && guest.trade_stats && (
                              <div className="mt-3 p-3 bg-white/5 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-white flex items-center gap-1">
                                    <BarChart3 className="w-4 h-4" /> Trade Statistics
                                  </span>
                                  <Select 
                                    value={selectedStatsPeriod[guest.id] || 'last_24h'} 
                                    onValueChange={(val) => setSelectedStatsPeriod({...selectedStatsPeriod, [guest.id]: val})}
                                  >
                                    <SelectTrigger className="w-28 h-7 text-xs bg-white/10 border-white/20 text-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-800 border-slate-700">
                                      <SelectItem value="last_24h" className="text-white text-xs">Last 24h</SelectItem>
                                      <SelectItem value="yesterday" className="text-white text-xs">Yesterday</SelectItem>
                                      <SelectItem value="this_week" className="text-white text-xs">This Week</SelectItem>
                                      <SelectItem value="all_time" className="text-white text-xs">All Time</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {(() => {
                                  const period = selectedStatsPeriod[guest.id] || 'last_24h';
                                  const stats = guest.trade_stats[period];
                                  return (
                                    <div className="grid grid-cols-4 gap-2">
                                      <div className="bg-white/10 rounded p-2 text-center">
                                        <div className="text-lg font-bold text-white">{stats?.trades || 0}</div>
                                        <div className="text-xs text-gray-400">Trades</div>
                                      </div>
                                      <div className="bg-green-500/20 rounded p-2 text-center">
                                        <div className="text-lg font-bold text-green-400">{stats?.wins || 0}</div>
                                        <div className="text-xs text-gray-400">Wins</div>
                                      </div>
                                      <div className="bg-red-500/20 rounded p-2 text-center">
                                        <div className="text-lg font-bold text-red-400">{stats?.losses || 0}</div>
                                        <div className="text-xs text-gray-400">Losses</div>
                                      </div>
                                      <div className={`rounded p-2 text-center ${
                                        stats?.win_rate >= 60 ? 'bg-green-500/20' :
                                        stats?.win_rate >= 40 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                                      }`}>
                                        <div className={`text-lg font-bold ${
                                          stats?.win_rate >= 60 ? 'text-green-400' :
                                          stats?.win_rate >= 40 ? 'text-yellow-400' : 'text-red-400'
                                        }`}>{stats?.win_rate || 0}%</div>
                                        <div className="text-xs text-gray-400">Win Rate</div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            
                            {/* Expanded Profile Details */}
                            {expandedProfile === guest.id && (
                              <div className="mt-3 p-3 bg-white/5 rounded-lg">
                                <div className="flex items-center gap-2 mb-3">
                                  <User className="w-4 h-4 text-indigo-400" />
                                  <span className="text-sm font-medium text-white">Profile Details</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Mailing Address */}
                                  <div className="bg-white/5 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <MapPin className="w-4 h-4 text-blue-400" />
                                      <span className="text-xs font-medium text-gray-300">Mailing Address</span>
                                    </div>
                                    {guest.mailing_address ? (
                                      <div className="text-xs text-gray-400 space-y-1">
                                        <p className="text-white">{guest.mailing_address.street}</p>
                                        <p>{guest.mailing_address.city}, {guest.mailing_address.state} {guest.mailing_address.zip_code}</p>
                                        <p>{guest.mailing_address.country}</p>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-yellow-400">No mailing address</p>
                                    )}
                                  </div>
                                  
                                  {/* Billing Address */}
                                  <div className="bg-white/5 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <CreditCard className="w-4 h-4 text-green-400" />
                                      <span className="text-xs font-medium text-gray-300">Billing Address</span>
                                      {guest.billing_same_as_mailing && (
                                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-gray-500/20 text-gray-400 border-gray-500/30">
                                          Same as mailing
                                        </Badge>
                                      )}
                                    </div>
                                    {guest.billing_address ? (
                                      <div className="text-xs text-gray-400 space-y-1">
                                        <p className="text-white">{guest.billing_address.street}</p>
                                        <p>{guest.billing_address.city}, {guest.billing_address.state} {guest.billing_address.zip_code}</p>
                                        <p>{guest.billing_address.country}</p>
                                        {guest.billing_address.phone && (
                                          <p className="flex items-center gap-1 text-blue-400">
                                            <Phone className="w-3 h-3" /> {guest.billing_address.phone}
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-yellow-400">No billing address</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Additional Info */}
                                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                  <div className="bg-white/5 rounded p-2">
                                    <span className="text-gray-500">Email Verified</span>
                                    <p className={guest.email_verified ? 'text-green-400' : 'text-yellow-400'}>
                                      {guest.email_verified ? 'Yes' : 'No'}
                                    </p>
                                  </div>
                                  <div className="bg-white/5 rounded p-2">
                                    <span className="text-gray-500">Password Set</span>
                                    <p className={guest.password_hash ? 'text-green-400' : 'text-yellow-400'}>
                                      {guest.password_hash ? 'Yes' : 'No'}
                                    </p>
                                  </div>
                                  <div className="bg-white/5 rounded p-2">
                                    <span className="text-gray-500">Profile Complete</span>
                                    <p className={guest.profile_complete ? 'text-green-400' : 'text-yellow-400'}>
                                      {guest.profile_complete ? 'Yes' : 'No'}
                                    </p>
                                  </div>
                                  <div className="bg-white/5 rounded p-2">
                                    <span className="text-gray-500">Last Activity</span>
                                    <p className="text-white">{guest.last_activity ? formatDate(guest.last_activity) : 'Never'}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {getStatusBadge(guest.status, guest.is_blocked)}
                            
                            {status === 'blocked' && (
                              <Button
                                size="sm"
                                onClick={() => handleUnblock(guest.id)}
                                disabled={processing === guest.id}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Unblock
                              </Button>
                            )}
                            
                            {status === 'pending_verification' && !guest.is_blocked && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleVerifyEmail(guest.id)}
                                  disabled={processing === guest.id}
                                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                                >
                                  <Mail className="w-4 h-4 mr-1" />
                                  Verify Email
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApproval(guest.id, 'reject')}
                                  disabled={processing === guest.id}
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                            
                            {status === 'pending_approval' && !guest.is_blocked && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleMarkAsPaid(guest.id)}
                                  disabled={processing === guest.id}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                  <DollarSign className="w-4 h-4 mr-1" />
                                  Mark as Paid
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleApproval(guest.id, 'approve')}
                                  disabled={processing === guest.id}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApproval(guest.id, 'reject')}
                                  disabled={processing === guest.id}
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}

                            {status === 'rejected' && !guest.is_blocked && (
                              <Button
                                size="sm"
                                onClick={() => handleApproval(guest.id, 'approve')}
                                disabled={processing === guest.id}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            )}

                            {status === 'approved' && !guest.is_blocked && (
                              <div className="flex gap-2">
                                {/* Top Performer Toggle */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleTopPerformer(guest.id, guest.is_top_performer)}
                                  disabled={processing === guest.id}
                                  className={`${
                                    guest.is_top_performer 
                                      ? 'border-yellow-500/50 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' 
                                      : 'border-gray-500/30 text-gray-400 hover:bg-gray-500/10'
                                  }`}
                                  title={guest.is_top_performer ? 'Remove from Top Performers' : 'Add to Top Performers'}
                                >
                                  <Trophy className="w-4 h-4 mr-1" />
                                  {guest.is_top_performer ? '⭐ Top' : 'Top'}
                                </Button>
                                {/* Force Payment Toggle */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleForcePayment(guest.id, guest.force_payment)}
                                  disabled={processing === guest.id}
                                  className={`${
                                    guest.force_payment 
                                      ? 'border-orange-500/50 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30' 
                                      : 'border-gray-500/30 text-gray-400 hover:bg-gray-500/10'
                                  }`}
                                  title={guest.force_payment ? 'Force payment is ON - user will be redirected to pay' : 'Force payment is OFF'}
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  {guest.force_payment ? 'Pay ON' : 'Pay'}
                                </Button>
                                {/* Grant Bonus Days Button */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setShowBonusModal(guest.id);
                                    setBonusDays(7);
                                  }}
                                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                                >
                                  <Gift className="w-4 h-4 mr-1" />
                                  +Days
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleBlock(guest.id, 'temporary')}
                                  disabled={processing === guest.id}
                                  className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                                >
                                  <Ban className="w-4 h-4 mr-1" />
                                  Pause
                                </Button>
                                {/* Message Button - replaces Block */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setMessengerUser(guest);
                                    setShowMessenger(true);
                                  }}
                                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                                >
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  Msg
                                </Button>
                                {/* Request Review Button */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => requestReview(guest.id, guest.name)}
                                  disabled={processing === guest.id}
                                  className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                                  title="Request a review from this user"
                                >
                                  <Star className="w-4 h-4 mr-1" />
                                  Review
                                </Button>
                                {/* Force Logout Button */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => forceLogoutUser(guest.id, guest.email)}
                                  disabled={processing === guest.id}
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                  title="Force logout - clears user session and cache"
                                >
                                  <LogOut className="w-4 h-4" />
                                </Button>
                              </div>
                            )}

                            {/* Delete Account Button - Available for all statuses */}
                            <div className="mt-2 pt-2 border-t border-white/5">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteGuest(guest.id, guest.email)}
                                disabled={processing === guest.id}
                                className="border-red-500/20 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40 text-xs"
                                title="Permanently delete this account"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete Account
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Back to Dashboard Link */}
      <div className="max-w-6xl mx-auto mt-8 text-center">
        <a 
          href="/"
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Back to Trading Dashboard
        </a>
      </div>

      {/* Grant Bonus Days Modal */}
      {showBonusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10">
            <button 
              onClick={() => setShowBonusModal(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-5">
              <div className="flex items-center gap-3">
                <Gift className="w-8 h-8 text-white" />
                <div>
                  <h2 className="text-xl font-bold text-white">Grant Extension Days</h2>
                  <p className="text-white/80 text-sm">
                    {guests.find(g => g.id === showBonusModal)?.email}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Current User Stats */}
              {(() => {
                const user = guests.find(g => g.id === showBonusModal);
                if (!user) return null;
                return (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-2xl font-bold text-white">{user.referral_count || 0}</p>
                        <p className="text-xs text-gray-400">Referrals</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-400">{user.bonus_days || 0}</p>
                        <p className="text-xs text-gray-400">Earned Days</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-yellow-400">{user.manual_extension_days || 0}</p>
                        <p className="text-xs text-gray-400">Extension</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Days Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">Days to Grant</label>
                <Select value={bonusDays.toString()} onValueChange={(v) => setBonusDays(parseInt(v))}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {[1, 3, 5, 7, 14, 21, 30].map((d) => (
                      <SelectItem key={d} value={d.toString()} className="text-white">
                        {d} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => grantBonusDays(showBonusModal, bonusDays)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Add {bonusDays} Days
                </Button>
                <Button
                  onClick={() => setExtensionDays(showBonusModal, bonusDays)}
                  variant="outline"
                  className="flex-1 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Set to {bonusDays}
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                "Add" extends from current expiry. "Set" overrides the extension.
              </p>

              {/* Quick Override Options */}
              <div className="pt-2 border-t border-white/10">
                <p className="text-sm text-gray-400 mb-2">Quick set:</p>
                <div className="flex gap-2 flex-wrap">
                  {[0, 7, 14, 30].map((d) => (
                    <Button
                      key={d}
                      size="sm"
                      variant="ghost"
                      onClick={() => setExtensionDays(showBonusModal, d)}
                      className="text-gray-400 hover:text-white hover:bg-white/10"
                    >
                      {d === 0 ? 'Reset' : `${d}d`}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ Marketing Broadcast Section ============ */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-slate-800/50 border-white/10">
          <CardHeader
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => {
              const next = !marketingCollapsed;
              setMarketingCollapsed(next);
              if (!next) {
                fetchMarketingAudience();
                fetchMarketingBroadcasts();
                fetchMarketingFavorites();
              }
            }}
            data-testid="marketing-broadcast-toggle"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500/30 to-rose-500/30">
                  <Send className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    Marketing Email Broadcast
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px]">AI</Badge>
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-xs">
                    Describe your promo, let AI write it, review, and broadcast to any user segment.
                  </CardDescription>
                </div>
              </div>
              {marketingCollapsed ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronUp className="w-5 h-5 text-gray-400" />}
            </div>
          </CardHeader>

          {!marketingCollapsed && (
            <CardContent className="space-y-6">
              {/* Step 1 - AI brief */}
              <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4 space-y-3">
                <p className="text-sm font-semibold text-amber-300 flex items-center gap-2">
                  <Megaphone className="w-4 h-4" /> Step 1 — Describe the promo
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-400 mb-1 block">Promo description</label>
                    <Textarea
                      value={mkDescription}
                      onChange={(e) => setMkDescription(e.target.value)}
                      placeholder="e.g. Black Friday 50% off the Pro AI Trading Bot. Limited to first 100 buyers, ends Sunday."
                      className="bg-slate-800 border-white/10 text-white min-h-[80px]"
                      data-testid="mk-description-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Keywords (comma separated)</label>
                    <Input
                      value={mkKeywords}
                      onChange={(e) => setMkKeywords(e.target.value)}
                      placeholder="black friday, pro bot, AI signals, exclusive"
                      className="bg-slate-800 border-white/10 text-white"
                      data-testid="mk-keywords-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tone</label>
                    <Select value={mkTone} onValueChange={setMkTone}>
                      <SelectTrigger className="bg-slate-800 border-white/10 text-white" data-testid="mk-tone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="exciting">Exciting</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">CTA URL (optional)</label>
                    <Input
                      value={mkCtaUrl}
                      onChange={(e) => setMkCtaUrl(e.target.value)}
                      placeholder="https://forthewisepro.com/store"
                      className="bg-slate-800 border-white/10 text-white"
                      data-testid="mk-cta-url-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">CTA button label (optional)</label>
                    <Input
                      value={mkCtaLabel}
                      onChange={(e) => setMkCtaLabel(e.target.value)}
                      placeholder="Claim 50% Off"
                      className="bg-slate-800 border-white/10 text-white"
                      data-testid="mk-cta-label-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={generateMarketingEmail}
                  disabled={mkGenerating}
                  className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white font-semibold"
                  data-testid="mk-generate-btn"
                >
                  {mkGenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating with GPT-4o…</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" /> Generate with AI</>
                  )}
                </Button>
              </div>

              {/* Step 2 - Review & edit */}
              {(mkSubject || mkBodyHtml) && (
                <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-emerald-300 flex items-center gap-2">
                      <Edit className="w-4 h-4" /> Step 2 — Review & edit
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={saveMarketingFavorite}
                      disabled={mkSavingFav}
                      className="border-amber-400/40 text-amber-300 hover:bg-amber-500/10 h-8"
                      data-testid="mk-save-favorite-btn-top"
                    >
                      {mkSavingFav ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Star className="w-3 h-3 mr-1" />}
                      Save as Favorite
                    </Button>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Subject line (with emojis)</label>
                    <Input
                      value={mkSubject}
                      onChange={(e) => setMkSubject(e.target.value)}
                      className="bg-slate-800 border-white/10 text-white"
                      data-testid="mk-subject-input"
                    />
                  </div>
                  {mkPreviewText && (
                    <p className="text-xs text-gray-500 italic">Inbox preview: {mkPreviewText}</p>
                  )}
                  <div className="grid lg:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">HTML body (editable)</label>
                      <Textarea
                        value={mkBodyHtml}
                        onChange={(e) => setMkBodyHtml(e.target.value)}
                        className="bg-slate-800 border-white/10 text-white font-mono text-xs min-h-[260px]"
                        data-testid="mk-body-textarea"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Live preview</label>
                      <div className="bg-white rounded-lg overflow-hidden border border-white/10" style={{ minHeight: '260px', maxHeight: '420px', overflowY: 'auto' }}>
                        <iframe
                          title="email-preview"
                          srcDoc={rebuildWrappedHtml(mkDebouncedBody)}
                          className="w-full"
                          style={{ minHeight: '420px', border: 'none' }}
                          data-testid="mk-preview-iframe"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3 - Audience & send */}
              {(mkSubject || mkBodyHtml) && (
                <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4 space-y-3">
                  <p className="text-sm font-semibold text-blue-300 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Step 3 — Choose audience & send
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Audience segment</label>
                      <Select value={mkSegment} onValueChange={setMkSegment}>
                        <SelectTrigger className="bg-slate-800 border-white/10 text-white" data-testid="mk-segment-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All users ({mkAudienceCounts.all || 0})</SelectItem>
                          <SelectItem value="approved">Approved users ({mkAudienceCounts.approved || 0})</SelectItem>
                          <SelectItem value="unapproved">Unapproved users ({mkAudienceCounts.unapproved || 0})</SelectItem>
                          <SelectItem value="pending_email_verification">Pending email verification ({mkAudienceCounts.pending_email_verification || 0})</SelectItem>
                          <SelectItem value="pending_payment">Pending payment ({mkAudienceCounts.pending_payment || 0})</SelectItem>
                          <SelectItem value="active_subscribers">Active subscribers ({mkAudienceCounts.active_subscribers || 0})</SelectItem>
                          <SelectItem value="expired_subscribers">Expired subscribers ({mkAudienceCounts.expired_subscribers || 0})</SelectItem>
                          <SelectItem value="paid_users">Paid users ({mkAudienceCounts.paid_users || 0})</SelectItem>
                          <SelectItem value="blocked">Blocked / paused ({mkAudienceCounts.blocked || 0})</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-amber-300 mt-1">
                        Recipients: <span className="font-bold">{mkAudienceCounts[mkSegment] || 0}</span>
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Send a test email first</label>
                      <div className="flex gap-2">
                        <Input
                          value={mkTestEmail}
                          onChange={(e) => setMkTestEmail(e.target.value)}
                          placeholder="your-inbox@example.com"
                          className="bg-slate-800 border-white/10 text-white"
                          data-testid="mk-test-email-input"
                        />
                        <Button
                          variant="outline"
                          onClick={() => sendMarketingBroadcast(true)}
                          disabled={mkSending}
                          className="border-blue-400/40 text-blue-300 hover:bg-blue-500/10 whitespace-nowrap"
                          data-testid="mk-send-test-btn"
                        >
                          {mkSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-white/10 flex flex-wrap items-center gap-3">
                    <Button
                      onClick={() => sendMarketingBroadcast(false)}
                      disabled={mkSending || !(mkAudienceCounts[mkSegment] > 0)}
                      className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white font-bold"
                      data-testid="mk-send-broadcast-btn"
                    >
                      {mkSending ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Approve & Send Broadcast</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={saveMarketingFavorite}
                      disabled={mkSavingFav || !mkSubject || !mkBodyHtml}
                      className="border-amber-400/40 text-amber-300 hover:bg-amber-500/10"
                      data-testid="mk-save-favorite-btn"
                    >
                      {mkSavingFav ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
                      Save as Favorite
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setMkSubject('');
                        setMkBodyHtml('');
                        setMkWrappedHtml('');
                        setMkPreviewText('');
                      }}
                      className="text-gray-400 hover:text-white"
                      data-testid="mk-clear-draft-btn"
                    >
                      Clear draft
                    </Button>
                  </div>
                </div>
              )}

              {/* Favorites */}
              <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4">
                <button
                  type="button"
                  onClick={() => setMkShowFavorites(!mkShowFavorites)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-300"
                  data-testid="mk-favorites-toggle"
                >
                  <span className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-400" /> Saved favorites ({mkFavorites.length})
                  </span>
                  {mkShowFavorites ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {mkShowFavorites && (
                  <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto">
                    {mkFavorites.length === 0 && (
                      <p className="text-xs text-gray-500 italic">No favorites yet. Generate a draft and click "Save as Favorite".</p>
                    )}
                    {mkFavorites.map((f) => (
                      <div key={f.id} className="bg-slate-800/60 border border-amber-500/10 rounded p-3 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-amber-300 font-semibold truncate">⭐ {f.name}</p>
                            <p className="text-white truncate">{f.subject}</p>
                            <p className="text-gray-500">Saved {new Date(f.created_at).toLocaleString()}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10"
                              onClick={() => loadMarketingFavorite(f)}
                              data-testid={`mk-load-favorite-${f.id}`}
                            >
                              Load
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-rose-300 hover:bg-rose-500/10"
                              onClick={() => deleteMarketingFavorite(f.id)}
                              data-testid={`mk-delete-favorite-${f.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* History */}
              <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4">
                <button
                  type="button"
                  onClick={() => setMkShowHistory(!mkShowHistory)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-300"
                  data-testid="mk-history-toggle"
                >
                  <span className="flex items-center gap-2">
                    <Archive className="w-4 h-4" /> Broadcast history ({mkBroadcasts.length})
                  </span>
                  {mkShowHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {mkShowHistory && (
                  <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto">
                    {mkBroadcasts.length === 0 && (
                      <p className="text-xs text-gray-500 italic">No broadcasts sent yet.</p>
                    )}
                    {mkBroadcasts.map((b) => (
                      <div key={b.id} className="bg-slate-800/60 border border-white/5 rounded p-3 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold truncate">{b.subject}</p>
                            <p className="text-gray-400">
                              Segment: <span className="text-amber-300">{b.segment}</span> &middot; Sent {b.sent_count}/{b.recipients_count}
                              {b.failed_count > 0 && <span className="text-rose-400"> &middot; {b.failed_count} failed</span>}
                            </p>
                            <p className="text-gray-500">{new Date(b.sent_at).toLocaleString()} by {b.sent_by}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-blue-300 hover:bg-blue-500/10"
                              onClick={() => setMkSelectedBroadcast(b)}
                              data-testid={`mk-view-broadcast-${b.id}`}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-rose-300 hover:bg-rose-500/10"
                              onClick={() => deleteBroadcastLog(b.id)}
                              data-testid={`mk-delete-broadcast-${b.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* ============ Trader Scorecards Admin Section ============ */}
      <div className="max-w-6xl mx-auto mb-8">
        <Card className="bg-slate-800/50 border-white/10">
          <CardHeader
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => {
              const next = !scCollapsed;
              setScCollapsed(next);
              if (!next && scUsers.length === 0) fetchScorecardUsers();
            }}
            data-testid="scorecard-admin-toggle"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500/30 to-yellow-500/30">
                  <Trophy className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    Trader Scorecards
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px]">AI Coach</Badge>
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-xs">
                    Review every user's live trading sessions, discipline scores, and AI evaluations.
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!scCollapsed && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-amber-300 hover:bg-amber-500/10"
                    onClick={(e) => { e.stopPropagation(); fetchScorecardUsers(); if (scSelectedUser) fetchScorecardUserSessions(scSelectedUser); }}
                    data-testid="scorecard-admin-refresh"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                  </Button>
                )}
                {scCollapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
              </div>
            </div>
          </CardHeader>
          {!scCollapsed && (
            <CardContent className="space-y-4">
              {scLoadingUsers ? (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading users…
                </div>
              ) : scUsers.length === 0 ? (
                <p className="text-sm text-gray-500 italic text-center py-4">No trader scorecard activity yet.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Users list */}
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="sc-admin-user-list">
                    <p className="text-xs uppercase tracking-wider text-amber-300 font-semibold">Users ({scUsers.length})</p>
                    {scUsers.map((u) => {
                      const active = scSelectedUser === u.email;
                      return (
                        <button
                          key={u.email}
                          onClick={() => fetchScorecardUserSessions(u.email)}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${active ? 'bg-amber-500/10 border-amber-400/60' : 'bg-slate-900/60 border-white/5 hover:border-amber-500/30'}`}
                          data-testid={`sc-admin-user-${u.email}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-white text-sm font-semibold truncate">{u.user_name || u.email.split('@')[0]}</p>
                              <p className="text-[11px] text-gray-400 truncate">{u.email}</p>
                            </div>
                            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] shrink-0">
                              {u.sessions_count} session{u.sessions_count === 1 ? '' : 's'}
                            </Badge>
                          </div>
                          {u.last_session && (
                            <p className="text-[10px] text-gray-500 mt-1">
                              Last: {new Date(u.last_session).toLocaleString()}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Sessions detail panel */}
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="sc-admin-sessions-panel">
                    {!scSelectedUser ? (
                      <div className="bg-slate-900/40 border border-dashed border-white/10 rounded-lg p-8 text-center text-gray-500 text-sm">
                        <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        Select a user to see their sessions and AI evaluations.
                      </div>
                    ) : scLoadingSessions ? (
                      <div className="text-center py-6 text-gray-400 text-sm">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading sessions…
                      </div>
                    ) : scSessions.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No sessions for this user.</p>
                    ) : (
                      <>
                        <p className="text-xs uppercase tracking-wider text-amber-300 font-semibold">
                          Sessions for {scSelectedUser} ({scSessions.length})
                        </p>
                        {scSessions.map((s) => {
                          const stats = s.stats || {};
                          const adminToken = localStorage.getItem('adminToken');
                          const pdfUrl = `${API}/admin/scorecard/session/${s.id}?token=${adminToken}`;
                          return (
                            <div key={s.id} className="bg-slate-900/60 border border-white/5 rounded-lg p-3 text-xs" data-testid={`sc-admin-session-${s.id}`}>
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2 min-w-0">
                                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] shrink-0">
                                    {(s.trading_style || '').toUpperCase()}
                                  </Badge>
                                  <span className="text-white truncate">{new Date(s.started_at).toLocaleString()}</span>
                                </div>
                                <a href={pdfUrl} target="_blank" rel="noreferrer"
                                  className="text-amber-300 hover:underline flex items-center gap-1 text-[11px] shrink-0" data-testid={`sc-admin-pdf-${s.id}`}>
                                  <FileText className="w-3 h-3" /> View
                                </a>
                              </div>
                              <div className="text-gray-400">
                                {stats.trades_count || 0} trades · {stats.wins || 0}W/{stats.losses || 0}L · Win {stats.win_rate || 0}% ·
                                <span className={(stats.total_pnl || 0) >= 0 ? ' text-emerald-400' : ' text-rose-400'}>
                                  {' '}PnL {(stats.total_pnl || 0) >= 0 ? '+' : ''}{stats.total_pnl || 0}
                                </span>
                              </div>
                              {s.ai_evaluation?.headline && (
                                <p className="text-amber-200/80 mt-1 text-[11px] line-clamp-2">🤖 {s.ai_evaluation.headline}</p>
                              )}
                              {s.session_goal && (
                                <p className="text-gray-500 mt-1 text-[10px] italic truncate">🎯 {s.session_goal}</p>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      {/* Broadcast detail modal */}
      {mkSelectedBroadcast && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setMkSelectedBroadcast(null)}>
          <div className="bg-slate-900 border border-white/10 rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="min-w-0">
                <p className="text-white font-semibold truncate">{mkSelectedBroadcast.subject}</p>
                <p className="text-xs text-gray-400">
                  {mkSelectedBroadcast.segment} &middot; {new Date(mkSelectedBroadcast.sent_at).toLocaleString()} &middot; {mkSelectedBroadcast.sent_count}/{mkSelectedBroadcast.recipients_count} sent
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setMkSelectedBroadcast(null)} className="text-gray-400">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <iframe
              title="broadcast-detail"
              srcDoc={mkSelectedBroadcast.html_body}
              className="w-full flex-1 bg-white"
              style={{ minHeight: '500px', border: 'none' }}
            />
          </div>
        </div>
      )}

      {/* Save Favorite Name Modal */}
      {mkFavNameModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !mkSavingFav && setMkFavNameModal(false)}>
          <div className="bg-slate-900 border border-amber-500/30 rounded-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="mk-fav-name-modal">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-semibold text-white">Name this favorite</h3>
            </div>
            <p className="text-sm text-gray-400">Give it a label so you can find it later (e.g., "Black Friday 50%").</p>
            <Input
              value={mkFavName}
              onChange={(e) => setMkFavName(e.target.value)}
              placeholder="My promo template"
              className="bg-slate-800 border-white/10 text-white"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') _doSaveFavorite(); }}
              data-testid="mk-fav-name-input"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setMkFavNameModal(false)} disabled={mkSavingFav} className="text-gray-400" data-testid="mk-fav-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={_doSaveFavorite}
                disabled={mkSavingFav || !mkFavName.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="mk-fav-confirm-save-btn"
              >
                {mkSavingFav ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
                Save Favorite
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Send Broadcast Modal */}
      {mkConfirmSend && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !mkSending && setMkConfirmSend(false)}>
          <div className="bg-slate-900 border border-emerald-500/30 rounded-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="mk-confirm-send-modal">
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold text-white">Confirm broadcast</h3>
            </div>
            <p className="text-sm text-gray-300">
              Send this email to <span className="font-bold text-emerald-300">{mkAudienceCounts[mkSegment] || 0}</span> {mkSegment.replace(/_/g, ' ')} recipient{(mkAudienceCounts[mkSegment] || 0) === 1 ? '' : 's'}?
            </p>
            <p className="text-xs text-gray-500 italic truncate">Subject: {mkSubject}</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setMkConfirmSend(false)} disabled={mkSending} className="text-gray-400" data-testid="mk-confirm-send-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => _doSendBroadcast(false)}
                disabled={mkSending}
                className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white"
                data-testid="mk-confirm-send-yes"
              >
                {mkSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Yes, send now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Broadcast Log Modal */}
      {mkConfirmDeleteBroadcast && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setMkConfirmDeleteBroadcast(null)}>
          <div className="bg-slate-900 border border-rose-500/30 rounded-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white">Delete broadcast log?</h3>
            <p className="text-sm text-gray-400">This only removes the history entry. Already-sent emails stay sent.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMkConfirmDeleteBroadcast(null)} className="text-gray-400">Cancel</Button>
              <Button onClick={_doDeleteBroadcastLog} className="bg-rose-500 hover:bg-rose-600 text-white">Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Favorite Modal */}
      {mkConfirmDeleteFavorite && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setMkConfirmDeleteFavorite(null)}>
          <div className="bg-slate-900 border border-rose-500/30 rounded-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white">Delete this favorite?</h3>
            <p className="text-sm text-gray-400">This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMkConfirmDeleteFavorite(null)} className="text-gray-400">Cancel</Button>
              <Button onClick={_doDeleteFavorite} className="bg-rose-500 hover:bg-rose-600 text-white">Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Messenger Modal */}
      <AdminMessengerModal
        isOpen={showMessenger}
        onClose={() => {
          setShowMessenger(false);
          setMessengerUser(null);
        }}
        user={messengerUser}
      />
    </div>
  );
};

export default AdminDashboard;
